import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  Plane,
  Upload,
  UserPlus,
  Mail,
  Settings,
  LogOut,
  Trash2,
  Copy,
  Users,
  FileText,
  UploadCloud,
  X,
  CheckCircle2,
  Loader2,
  Bell,
  Send,
  Sparkles,
  Search,
  Filter,
  MessageCircle,
  Home,
  TrendingUp,
  UserCog,
  Shield,
  Pencil,
  Eye,
  AlertTriangle,
} from "lucide-react";
import { SellerRankingCard } from "../components/admin/SellerRankingCard";
import { VendedoresSection } from "../components/admin/VendedoresSection";
import { UserDialog } from "../components/admin/UserDialog";
import { TripDialog } from "../components/admin/TripDialog";
import { TripsPanel } from "../components/admin/TripsPanel";
import { DuplicateClientDialog } from "../components/admin/DuplicateClientDialog";
import { TripsMigrationCard } from "../components/admin/TripsMigrationCard";
import { ReturnsThisWeekCard } from "../components/admin/ReturnsThisWeekCard";
import { PostTripBar } from "../components/admin/PostTripBar";
import { PostTripTemplateCard } from "../components/admin/PostTripTemplateCard";
import { GeneralTemplateCard } from "../components/admin/GeneralTemplateCard";
import { ReminderPreviewDialog } from "../components/admin/ReminderPreviewDialog";
import { TemplateVarsHint } from "../components/admin/TemplateVarsHint";
import { RichTextEditor } from "../components/admin/RichTextEditor";
import { VoucherTypesCard } from "../components/admin/VoucherTypesCard";
import { useVoucherTypes, VoucherTypeIcon } from "../lib/voucherTypes";
import { fmtDate, toInputDate, fromInputDate, tripTitle, tripDatesLabel, daysUntil, TRIP_TYPE_EMOJI } from "../lib/trips";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;


const TRIP_TYPES = [
  { value: "vuelo", label: "Vuelo ✈️" },
  { value: "paquete", label: "Paquete 🌴" },
  { value: "circuito", label: "Circuito 🗺️" },
  { value: "crucero", label: "Crucero 🚢" },
  { value: "otros", label: "Otros 🧳" },
];

export default function AdminDashboard({ onLogout }) {
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedTrip, setSelectedTrip] = useState(null);
  const [tripDialog, setTripDialog] = useState({ open: false, trip: null });
  const [duplicateInfo, setDuplicateInfo] = useState(null);
  const [activeTab, setActiveTab] = useState("clients");
  const [vouchers, setVouchers] = useState([]);
  const [logo, setLogo] = useState(null);
  const [agencyName, setAgencyName] = useState("Mi Agencia de Viajes");
  const [newClientDialog, setNewClientDialog] = useState(false);
  const [voucherDialog, setVoucherDialog] = useState(false);
  const [settingsDialog, setSettingsDialog] = useState(false);
  const [notesDialog, setNotesDialog] = useState(false);
  const [reminderDialog, setReminderDialog] = useState(false);
  const [clientNotes, setClientNotes] = useState("");
  const [loading, setLoading] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  const [voucherDragIdx, setVoucherDragIdx] = useState(null);
  const voucherTypes = useVoucherTypes();
  const detectVoucherType = voucherTypes.detect;

  const handleVoucherDragStart = (idx) => setVoucherDragIdx(idx);
  const handleVoucherDragOver = (e) => e.preventDefault();
  const handleVoucherDrop = async (dropIdx) => {
    if (voucherDragIdx === null || voucherDragIdx === dropIdx) {
      setVoucherDragIdx(null);
      return;
    }
    const next = [...vouchers];
    const [moved] = next.splice(voucherDragIdx, 1);
    next.splice(dropIdx, 0, moved);
    setVouchers(next);
    setVoucherDragIdx(null);
    try {
      await axios.put(
        `${API}/vouchers/reorder`,
        { voucher_ids: next.map((v) => v.id) },
        axiosConfig
      );
      toast.success("Orden guardado");
    } catch (error) {
      toast.error("Error al guardar orden");
      if (selectedClient) loadVouchers(selectedTrip?.id);
    }
  };

  // Edit client dialog
  const [editClientDialog, setEditClientDialog] = useState(false);
  const [editClientForm, setEditClientForm] = useState({
    id: "",
    name: "",
    last_name: "",
    email: "",
    phone: "",
  });

  const openEditClient = (client) => {
    setEditClientForm({
      id: client.id,
      name: client.name || "",
      last_name: client.last_name || "",
      email: client.email || "",
      phone: client.phone || "",
    });
    setEditClientDialog(true);
  };

  const handleSaveEditClient = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.put(
        `${API}/clients/${editClientForm.id}`,
        {
          name: editClientForm.name,
          last_name: editClientForm.last_name || null,
          email: editClientForm.email,
          phone: editClientForm.phone || null,
        },
        axiosConfig
      );
      toast.success("Cliente actualizado");
      setEditClientDialog(false);
      loadClients();
      if (selectedClient?.id === editClientForm.id) {
        setSelectedClient((c) => ({ ...c, ...editClientForm }));
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al actualizar cliente");
    } finally {
      setLoading(false);
    }
  };

  // Clientes sub-tab (list vs database table)
  const [clientesTab, setClientesTab] = useState("list");

  const exportClientsCSV = () => {
    const rows = [["Nº", "Nombre", "Apellido", "Email", "Teléfono"]];
    filteredClients.forEach((c) => {
      rows.push([
        c.client_number ?? "",
        (c.name || "").replaceAll('"', '""'),
        (c.last_name || "").replaceAll('"', '""'),
        c.email || "",
        c.phone || "",
      ]);
    });
    const csv = rows
      .map((r) => r.map((cell) => `"${cell}"`).join(","))
      .join("\n");
    // BOM for Excel UTF-8 support
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `clientes_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    toast.success(`Exportados ${filteredClients.length} clientes`);
  };

  // Destination templates state
  const [destTemplates, setDestTemplates] = useState([]);
  const EMPTY_DEST_FORM = { id: "", kind: "destination", name: "", destination: "", message: "" };
  const [destTemplateForm, setDestTemplateForm] = useState(EMPTY_DEST_FORM);
  const templateLabel = (dt) => (dt.kind === "general" ? dt.name : dt.destination);

  const loadDestTemplates = async () => {
    try {
      const res = await axios.get(`${API}/destination-templates`, axiosConfig);
      setDestTemplates(res.data.templates || []);
    } catch (error) {
      console.error("Error loading destination templates");
    }
  };

  const handleSaveDestTemplate = async () => {
    const isGeneral = destTemplateForm.kind === "general";
    if (!(isGeneral ? destTemplateForm.name : destTemplateForm.destination) || !destTemplateForm.message) {
      toast.error(isGeneral ? "Completá nombre y mensaje" : "Completá destino y mensaje");
      return;
    }
    const body = {
      kind: destTemplateForm.kind,
      name: isGeneral ? destTemplateForm.name : null,
      destination: isGeneral ? "" : destTemplateForm.destination,
      message: destTemplateForm.message,
    };
    try {
      if (destTemplateForm.id) {
        await axios.put(`${API}/destination-templates/${destTemplateForm.id}`, body, axiosConfig);
        toast.success("Plantilla actualizada");
      } else {
        await axios.post(`${API}/destination-templates`, body, axiosConfig);
        toast.success("Plantilla creada");
      }
      setDestTemplateForm(EMPTY_DEST_FORM);
      loadDestTemplates();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al guardar");
    }
  };

  const handleDeleteDestTemplate = async (id) => {
    if (!window.confirm("¿Eliminar esta plantilla?")) return;
    try {
      await axios.delete(`${API}/destination-templates/${id}`, axiosConfig);
      toast.success("Plantilla eliminada");
      loadDestTemplates();
    } catch (error) {
      toast.error("Error al eliminar");
    }
  };

  useEffect(() => {
    loadDestTemplates();
    // eslint-disable-next-line
  }, []);

  // Reminder state
  const [reminderForm, setReminderForm] = useState({
    trip_type: "",
    trip_date: "",
    return_date: "",
    trip_destination: "",
    trip_details: "",
    reminder_message: "",
    reminder_days_before: 7,
    reminder_enabled: false,
    reminder_channel: "email",
    phone: "",
    phone_secondary: "",
    extra_emails: [],
    destination_template_id: "",
  });
  const [reminderSentAt, setReminderSentAt] = useState(null);

  // Client search + filter
  const [searchQuery, setSearchQuery] = useState("");
  const [clientFilter, setClientFilter] = useState("all"); // all | reminder | no_vouchers | has_vouchers
  const [sellerFilter, setSellerFilter] = useState("all"); // all | unassigned | <seller_id>
  const [reminderSellerFilter, setReminderSellerFilter] = useState("all");
  const [notifySellerOnDownload, setNotifySellerOnDownload] = useState(true);
  const [notifySellerLoading, setNotifySellerLoading] = useState(false);

  // Current admin + Users/Sellers management
  const [currentAdmin, setCurrentAdmin] = useState(null);
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [userDialog, setUserDialog] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [userForm, setUserForm] = useState({ username: "", email: "", full_name: "", password: "", role: "seller" });

  // Voucher upload state
  const [voucherType, setVoucherType] = useState("");
  const [autoDetectType, setAutoDetectType] = useState(true);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState({});
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);

  const token = localStorage.getItem("admin_token");
  const axiosConfig = { headers: { Authorization: `Bearer ${token}` } };

  useEffect(() => {
    loadClients();
    loadSettings();
    loadUltramsg();
    loadCurrentAdmin();
  }, []);

  useEffect(() => {
    if (!selectedClient) {
      setSelectedTrip(null);
      return;
    }
    setClientNotes(selectedClient.notes || "");
    const trips = selectedClient.trips || [];
    setSelectedTrip((prev) => {
      const fresh = prev && trips.find((t) => t.id === prev.id);
      if (fresh) return fresh;
      return trips.find((t) => t.id === selectedClient.current_trip_id) || trips[0] || null;
    });
  }, [selectedClient]);

  useEffect(() => {
    if (!selectedTrip) {
      setVouchers([]);
      return;
    }
    loadVouchers(selectedTrip.id);
    setReminderForm({
      trip_type: selectedTrip.trip_type || "",
      trip_date: toInputDate(selectedTrip.trip_date),
      return_date: toInputDate(selectedTrip.return_date),
      trip_destination: selectedTrip.trip_destination || "",
      trip_details: selectedTrip.trip_details || "",
      reminder_message: selectedTrip.reminder_message || "",
      reminder_days_before: selectedTrip.reminder_days_before ?? 7,
      reminder_enabled: !!selectedTrip.reminder_enabled,
      reminder_channel: selectedTrip.reminder_channel || "email",
      phone: selectedClient?.phone || "",
      phone_secondary: selectedClient?.phone_secondary || "",
      extra_emails: selectedClient?.extra_emails || [],
      destination_template_id: selectedTrip.destination_template_id || "",
    });
    setReminderSentAt(selectedTrip.reminder_sent_at || null);
    // eslint-disable-next-line
  }, [selectedTrip]);

  const loadClients = async () => {
    try {
      const response = await axios.get(`${API}/clients`, axiosConfig);
      setClients(response.data);
      setSelectedClient((prev) => (prev ? response.data.find((c) => c.id === prev.id) || null : prev));
    } catch (error) {
      toast.error("Error al cargar clientes");
    }
  };

  const loadCurrentAdmin = async () => {
    try {
      const res = await axios.get(`${API}/auth/me`, axiosConfig);
      setCurrentAdmin(res.data);
      // Fire follow-up calls in parallel (users only for admins)
      const isAdmin = (res.data?.role || "admin") === "admin";
      const tasks = [
        axios.get(`${API}/settings/notifications`, axiosConfig).catch(() => null),
      ];
      if (isAdmin) tasks.push(axios.get(`${API}/users`, axiosConfig).catch(() => null));
      const results = await Promise.all(tasks);
      const notifRes = results[0];
      const usersRes = isAdmin ? results[1] : null;
      if (notifRes?.data) setNotifySellerOnDownload(!!notifRes.data.notify_seller_on_download);
      if (usersRes?.data) setUsers(usersRes.data);
    } catch (error) {
      console.error("Error loading current admin");
    }
  };

  const handleToggleNotifySeller = async (enabled) => {
    setNotifySellerLoading(true);
    // optimistic UI
    setNotifySellerOnDownload(enabled);
    try {
      await axios.put(
        `${API}/settings/notifications`,
        { notify_seller_on_download: enabled },
        axiosConfig
      );
      toast.success(enabled ? "Avisos activados" : "Avisos desactivados");
    } catch (error) {
      // rollback
      setNotifySellerOnDownload(!enabled);
      toast.error(error.response?.data?.detail || "Error al guardar");
    } finally {
      setNotifySellerLoading(false);
    }
  };

  const loadUsers = async () => {
    setUsersLoading(true);
    try {
      const res = await axios.get(`${API}/users`, axiosConfig);
      setUsers(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al cargar usuarios");
    } finally {
      setUsersLoading(false);
    }
  };

  const openNewUser = () => {
    setEditingUser(null);
    setUserForm({ username: "", email: "", full_name: "", password: "", role: "seller" });
    setUserDialog(true);
  };

  const openEditUser = (u) => {
    setEditingUser(u);
    setUserForm({
      username: u.username,
      email: u.email || "",
      full_name: u.full_name || "",
      password: "",
      role: u.role || "seller",
    });
    setUserDialog(true);
  };

  const handleSaveUser = async () => {
    try {
      if (editingUser) {
        const payload = {
          email: userForm.email,
          full_name: userForm.full_name,
          role: userForm.role,
        };
        if (userForm.password) payload.password = userForm.password;
        await axios.put(`${API}/users/${editingUser.id}`, payload, axiosConfig);
        toast.success("Usuario actualizado");
      } else {
        if (!userForm.username || !userForm.email || !userForm.password) {
          toast.error("Usuario, email y contraseña son requeridos");
          return;
        }
        await axios.post(`${API}/users`, userForm, axiosConfig);
        toast.success("Usuario creado");
      }
      setUserDialog(false);
      loadUsers();
      loadClients();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al guardar usuario");
    }
  };

  const handleDeleteUser = async (u) => {
    if (!window.confirm(`¿Eliminar el usuario "${u.username}"? Los clientes asignados quedarán sin vendedor.`)) return;
    try {
      await axios.delete(`${API}/users/${u.id}`, axiosConfig);
      toast.success("Usuario eliminado");
      loadUsers();
      loadClients();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al eliminar usuario");
    }
  };

  const handleAssignSeller = async (clientId, newSellerId) => {
    try {
      await axios.put(
        `${API}/clients/${clientId}`,
        { seller_id: newSellerId === "unassigned" ? null : newSellerId },
        axiosConfig
      );
      toast.success("Vendedor asignado");
      loadClients();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al asignar vendedor");
    }
  };

  const loadVouchers = async (tripId) => {
    if (!tripId) return;
    try {
      const response = await axios.get(`${API}/trips/${tripId}/vouchers`, axiosConfig);
      setVouchers(response.data);
    } catch (error) {
      toast.error("Error al cargar vouchers");
    }
  };

  const loadSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      setAgencyName(response.data.agency_name);
      const hasLogo = response.data.logo_gridfs_id || response.data.logo_filename;
      if (hasLogo) {
        setLogo(`${API}/settings/logo?t=${Date.now()}`);
      } else {
        setLogo(null);
      }
    } catch (error) {
      console.error("Error loading settings");
    }
  };

  const handleCreateClient = async (e) => {
    e.preventDefault();
    setLoading(true);
    const formData = new FormData(e.target);
    const tripData = {
      trip_destination: formData.get("trip_destination") || null,
      trip_date: fromInputDate(formData.get("trip_date")),
      return_date: fromInputDate(formData.get("return_date")),
    };

    try {
      const res = await axios.post(
        `${API}/clients`,
        {
          name: formData.get("name"),
          last_name: formData.get("last_name") || null,
          email: formData.get("email"),
          phone: formData.get("phone") || null,
          ...tripData,
        },
        axiosConfig
      );
      toast.success("Cliente creado exitosamente");
      setNewClientDialog(false);
      e.target.reset();
      openClient({ ...res.data, status: "active" }, res.data.trips?.[0] || null);
      loadClients();
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (error.response?.status === 409 && detail?.code === "duplicate_client") {
        setDuplicateInfo({ ...detail, prefill: tripData });
      } else {
        toast.error(typeof detail === "string" ? detail : "Error al crear cliente");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateNotes = async () => {
    if (!selectedClient) return;
    setLoading(true);
    try {
      await axios.put(
        `${API}/clients/${selectedClient.id}`,
        { notes: clientNotes },
        axiosConfig
      );
      toast.success("Notas guardadas exitosamente");
      setNotesDialog(false);
      loadClients();
      setSelectedClient({ ...selectedClient, notes: clientNotes });
    } catch (error) {
      toast.error("Error al guardar notas");
    } finally {
      setLoading(false);
    }
  };

  // ============== TRIPS ==============
  const openClient = (client, trip = null) => {
    setSelectedTrip(trip);
    setSelectedClient(client);
    setActiveSection("vouchers");
  };

  const openTripReminder = (client, trip) => {
    setSelectedTrip(trip);
    setSelectedClient(client);
    setReminderDialog(true);
  };

  const handleDuplicateAddTrip = () => {
    const existing = clients.find((c) => c.id === duplicateInfo.client_id);
    const prefill = duplicateInfo.prefill;
    setDuplicateInfo(null);
    setNewClientDialog(false);
    if (!existing) {
      toast.error("No se encontró el cliente existente");
      return;
    }
    openClient(existing);
    setTripDialog({ open: true, trip: prefill });
  };

  const handleSaveTrip = async (payload) => {
    try {
      const editing = tripDialog.trip?.id;
      const res = editing
        ? await axios.put(`${API}/trips/${editing}`, payload, axiosConfig)
        : await axios.post(`${API}/clients/${selectedClient.id}/trips`, payload, axiosConfig);
      toast.success(editing ? "Viaje actualizado" : "Viaje creado");
      setTripDialog({ open: false, trip: null });
      setSelectedTrip(res.data);
      loadClients();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al guardar el viaje");
    }
  };

  const handleDeleteTrip = async (trip) => {
    if (!window.confirm(`¿Eliminar el viaje "${tripTitle(trip)}" y sus vouchers? El cliente no se borra.`)) return;
    try {
      await axios.delete(`${API}/trips/${trip.id}`, axiosConfig);
      toast.success("Viaje eliminado");
      if (selectedTrip?.id === trip.id) setSelectedTrip(null);
      loadClients();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al eliminar el viaje");
    }
  };

  // ============== TRIP REMINDERS ==============
  const tripPayload = () => ({
    trip_type: reminderForm.trip_type || null,
    trip_date: fromInputDate(reminderForm.trip_date),
    return_date: fromInputDate(reminderForm.return_date),
    trip_destination: reminderForm.trip_destination || null,
    trip_details: reminderForm.trip_details || null,
    reminder_days_before: Number(reminderForm.reminder_days_before) || 7,
  });

  const handleGenerateTemplate = async () => {
    const dt = destTemplates.find((x) => x.id === reminderForm.destination_template_id);
    try {
      const text = dt
        ? dt.message
        : (await axios.get(`${API}/settings/general-template`, axiosConfig)).data.template;
      setReminderForm((f) => ({ ...f, reminder_message: text }));
      toast.success(`Plantilla "${dt ? templateLabel(dt) : "General"}" cargada`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al cargar la plantilla");
    }
  };

  const fetchReminderPreview = async () => {
    await saveReminderForm();
    const res = await axios.post(
      `${API}/trips/${selectedTrip.id}/reminder/preview`,
      { frontend_url: window.location.origin },
      axiosConfig
    );
    return res.data;
  };

  const handlePreviewReminder = async () => {
    if (!selectedTrip) return;
    try {
      setReminderPreview(await fetchReminderPreview());
      loadClients();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al generar la vista previa");
    }
  };

  const saveReminderForm = async () => {
    if (!selectedTrip) return null;
    const res = await axios.put(
      `${API}/trips/${selectedTrip.id}`,
      {
        ...tripPayload(),
        reminder_message: reminderForm.reminder_message || null,
        reminder_enabled: !!reminderForm.reminder_enabled,
        reminder_channel: reminderForm.reminder_channel || "email",
        destination_template_id: reminderForm.destination_template_id || null,
      },
      axiosConfig
    );
    await axios.put(
      `${API}/clients/${selectedClient.id}`,
      {
        phone: reminderForm.phone || null,
        phone_secondary: reminderForm.phone_secondary || null,
        extra_emails: (reminderForm.extra_emails || [])
          .map((e) => (e || "").trim())
          .filter((e) => e.length > 0),
      },
      axiosConfig
    );
    return res.data;
  };

  const handleSaveReminder = async () => {
    if (!selectedTrip) return;
    setLoading(true);
    try {
      await saveReminderForm();
      toast.success("Recordatorio guardado");
      loadClients();
      setReminderDialog(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al guardar recordatorio");
    } finally {
      setLoading(false);
    }
  };

  const handleSendReminderNow = async () => {
    if (!selectedTrip) return;
    setLoading(true);
    try {
      await saveReminderForm(); // persist before sending
      const res = await axios.post(`${API}/trips/${selectedTrip.id}/reminder/send`, {}, axiosConfig);
      setReminderSentAt(res.data.sent_at);
      toast.success("Recordatorio enviado por email");
      loadClients();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al enviar recordatorio");
    } finally {
      setLoading(false);
    }
  };

  const handleSendReminderUltraMsg = async () => {
    if (!selectedTrip) return;
    setLoading(true);
    try {
      await saveReminderForm();
      const res = await axios.post(`${API}/trips/${selectedTrip.id}/reminder/send-whatsapp`, {}, axiosConfig);
      setReminderSentAt(res.data.sent_at);
      toast.success("WhatsApp enviado vía UltraMsg");
      loadClients();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error enviando vía UltraMsg");
    } finally {
      setLoading(false);
    }
  };

  const handleSendReminderWhatsApp = async () => {
    if (!selectedTrip) return;
    const phone = (reminderForm.phone || "").replace(/\D/g, "");
    if (!phone) {
      toast.error("Agregá el teléfono del cliente (con código de país) para enviar por WhatsApp");
      return;
    }
    try {
      // Save form (including phone) and render the final message before opening WhatsApp
      const preview = await fetchReminderPreview();
      if (preview.problems.length > 0) {
        setReminderPreview(preview);
        toast.error("El mensaje tiene variables sin reemplazar. Corregilo antes de enviar.");
        return;
      }
      const text = encodeURIComponent(preview.message);
      // api.whatsapp.com keeps emojis intact (wa.me's redirect corrupts them)
      window.open(`https://api.whatsapp.com/send?phone=${phone}&text=${text}`, "_blank", "noopener,noreferrer");
      // Mark as sent in backend so reminder_sent_at reflects the manual WhatsApp send
      const res = await axios.post(`${API}/trips/${selectedTrip.id}/reminder/mark-sent`, {}, axiosConfig);
      setReminderSentAt(res.data.sent_at);
      toast.success("WhatsApp abierto — marcado como enviado");
      loadClients();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al abrir WhatsApp");
    }
  };
  // =============================================

  // ============== VOUCHER UPLOAD (multi-file + drag/drop) ==============
  const openVoucherDialog = () => {
    setVoucherType("");
    setSelectedFiles([]);
    setUploadProgress({});
    setVoucherDialog(true);
  };

  const addFiles = (fileList) => {
    const pdfs = Array.from(fileList).filter((f) =>
      f.name.toLowerCase().endsWith(".pdf")
    );
    if (pdfs.length === 0) {
      toast.error("Solo se aceptan archivos PDF");
      return;
    }
    if (pdfs.length !== fileList.length) {
      toast.warning("Algunos archivos no son PDF y fueron ignorados");
    }
    setSelectedFiles((prev) => [...prev, ...pdfs]);
  };

  const removeFile = (idx) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") setDragActive(true);
    else if (e.type === "dragleave") setDragActive(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      addFiles(e.dataTransfer.files);
    }
  };

  const handleUploadVouchers = async (e) => {
    e.preventDefault();
    if (!selectedTrip) return;
    if (selectedFiles.length === 0) {
      toast.error("Selecciona al menos un archivo PDF");
      return;
    }
    if (!autoDetectType && !voucherType) {
      toast.error("Selecciona el tipo de voucher o activá la detección automática");
      return;
    }

    setLoading(true);
    let okCount = 0;
    let errCount = 0;

    for (let i = 0; i < selectedFiles.length; i++) {
      const file = selectedFiles[i];
      setUploadProgress((p) => ({ ...p, [i]: "uploading" }));

      const perFileType = autoDetectType
        ? detectVoucherType(file.name) || voucherType || "otros"
        : voucherType;

      const formData = new FormData();
      formData.append("trip_id", selectedTrip.id);
      formData.append("voucher_type", perFileType);
      formData.append("file", file);

      try {
        await axios.post(`${API}/vouchers`, formData, {
          ...axiosConfig,
          headers: { ...axiosConfig.headers, "Content-Type": "multipart/form-data" },
        });
        setUploadProgress((p) => ({ ...p, [i]: "done" }));
        okCount++;
      } catch (error) {
        setUploadProgress((p) => ({ ...p, [i]: "error" }));
        errCount++;
      }
    }

    setLoading(false);
    if (okCount > 0) {
      toast.success(
        `${okCount} voucher${okCount > 1 ? "s subidos" : " subido"} correctamente`
      );
    }
    if (errCount > 0) {
      toast.error(`${errCount} archivo${errCount > 1 ? "s fallaron" : " falló"} al subir`);
    }

    loadVouchers(selectedTrip?.id);
    loadClients();
    // Keep dialog open if errors so user sees which failed
    if (errCount === 0) {
      setTimeout(() => {
        setVoucherDialog(false);
        setSelectedFiles([]);
        setUploadProgress({});
      }, 800);
    }
  };
  // ====================================================================

  const handleSendEmail = async (clientId, tripId) => {
    setLoading(true);
    try {
      // Pass current origin so email links use the SAME domain the admin is on
      await axios.post(
        `${API}/send-email`,
        { client_id: clientId, trip_id: tripId || null, frontend_url: window.location.origin },
        axiosConfig
      );
      toast.success("Email enviado exitosamente");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al enviar email");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClient = async (clientId) => {
    if (!window.confirm("¿Estás seguro de eliminar este cliente y todos sus vouchers?"))
      return;
    try {
      await axios.delete(`${API}/clients/${clientId}`, axiosConfig);
      toast.success("Cliente eliminado");
      if (selectedClient?.id === clientId) {
        setSelectedClient(null);
        setSelectedTrip(null);
        setVouchers([]);
        setActiveTab("clients");
      }
      loadClients();
    } catch (error) {
      toast.error("Error al eliminar cliente");
    }
  };

  const handleDeleteVoucher = async (voucherId) => {
    if (!window.confirm("¿Estás seguro de eliminar este voucher?")) return;
    try {
      await axios.delete(`${API}/vouchers/${voucherId}`, axiosConfig);
      toast.success("Voucher eliminado");
      loadVouchers(selectedTrip?.id);
      loadClients();
    } catch (error) {
      toast.error("Error al eliminar voucher");
    }
  };

  const handleUploadLogo = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error("El logo es muy grande (máx. 5MB)");
      return;
    }

    const formData = new FormData();
    formData.append("file", file);

    setUploadingLogo(true);
    try {
      await axios.post(`${API}/settings/logo`, formData, {
        ...axiosConfig,
        headers: { ...axiosConfig.headers, "Content-Type": "multipart/form-data" },
      });
      toast.success("Logo actualizado exitosamente");
      await loadSettings();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al subir logo");
    } finally {
      setUploadingLogo(false);
      // reset input so same file can be re-uploaded
      e.target.value = "";
    }
  };

  const handleUpdateAgencyName = async (e) => {
    e.preventDefault();
    const formData = new FormData();
    formData.append("agency_name", agencyName);

    try {
      await axios.put(`${API}/settings/agency-name`, formData, axiosConfig);
      toast.success("Nombre actualizado");
      setSettingsDialog(false);
    } catch (error) {
      toast.error("Error al actualizar nombre");
    }
  };

  const copyClientLink = (clientToken) => {
    const link = `${window.location.origin}/client/${clientToken}`;
    navigator.clipboard.writeText(link);
    toast.success("Link copiado al portapapeles");
  };

  const getVoucherTypeLabel = (type) => voucherTypes.byKey[type]?.label || type;

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // ============== SIDEBAR SECTIONS + TEMPLATES ==============
  const [activeSection, setActiveSection] = useState("dashboard");
  const [reminderPreview, setReminderPreview] = useState(null);
  const [reminderIssues, setReminderIssues] = useState({});

  // Email templates
  const [emailTemplates, setEmailTemplates] = useState(null);
  const [emailDefaults, setEmailDefaults] = useState(null);

  // SMTP
  const [smtpForm, setSmtpForm] = useState({
    gmail_user: "",
    gmail_app_password: "",
  });
  const [smtpInfo, setSmtpInfo] = useState({ has_password: false, using_env_fallback: false, password_masked: "" });
  const [smtpTestEmail, setSmtpTestEmail] = useState("");
  const [smtpLoading, setSmtpLoading] = useState(false);

  const loadSmtp = async () => {
    try {
      const res = await axios.get(`${API}/settings/smtp`, axiosConfig);
      setSmtpInfo(res.data);
      setSmtpForm({ gmail_user: res.data.gmail_user || "", gmail_app_password: "" });
    } catch (error) {
      console.error("Error loading smtp");
    }
  };

  const handleSaveSmtp = async () => {
    try {
      const payload = { gmail_user: smtpForm.gmail_user };
      if (smtpForm.gmail_app_password) payload.gmail_app_password = smtpForm.gmail_app_password;
      await axios.put(`${API}/settings/smtp`, payload, axiosConfig);
      toast.success("SMTP guardado");
      setSmtpForm((f) => ({ ...f, gmail_app_password: "" }));
      loadSmtp();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al guardar SMTP");
    }
  };

  const handleTestSmtp = async () => {
    if (!smtpTestEmail) {
      toast.error("Ingresá un email de destino para la prueba");
      return;
    }
    setSmtpLoading(true);
    try {
      const res = await axios.post(`${API}/settings/smtp/test`, { to: smtpTestEmail }, axiosConfig);
      toast.success(res.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error en test SMTP");
    } finally {
      setSmtpLoading(false);
    }
  };
  const [ultramsgForm, setUltramsgForm] = useState({
    instance_id: "",
    token: "",
    enabled: false,
  });
  const [ultramsgInfo, setUltramsgInfo] = useState({ has_token: false, token_masked: "" });
  const [ultramsgStatus, setUltramsgStatus] = useState(null);
  const [configTab, setConfigTab] = useState("agencia");

  const loadEmailTemplates = async () => {
    try {
      const res = await axios.get(`${API}/settings/email-templates`, axiosConfig);
      setEmailTemplates(res.data.templates);
      setEmailDefaults(res.data.defaults);
    } catch (error) {
      toast.error("Error al cargar plantillas de email");
    }
  };

  const handleSaveEmailTemplates = async () => {
    try {
      await axios.put(
        `${API}/settings/email-templates`,
        { templates: emailTemplates },
        axiosConfig
      );
      toast.success("Plantillas de email guardadas");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al guardar");
    }
  };

  const handleResetEmailTemplate = (key) => {
    if (!emailDefaults) return;
    setEmailTemplates((t) => ({ ...t, [key]: emailDefaults[key] }));
  };

  const loadUltramsg = async () => {
    try {
      const res = await axios.get(`${API}/settings/ultramsg`, axiosConfig);
      setUltramsgInfo(res.data);
      setUltramsgForm({
        instance_id: res.data.instance_id || "",
        token: "",
        enabled: !!res.data.enabled,
      });
    } catch (error) {
      console.error("Error loading ultramsg");
    }
  };

  const handleSaveUltramsg = async () => {
    try {
      const payload = {
        instance_id: ultramsgForm.instance_id,
        enabled: ultramsgForm.enabled,
      };
      if (ultramsgForm.token) payload.token = ultramsgForm.token;
      await axios.put(`${API}/settings/ultramsg`, payload, axiosConfig);
      toast.success("UltraMsg guardado");
      setUltramsgForm((f) => ({ ...f, token: "" }));
      loadUltramsg();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al guardar UltraMsg");
    }
  };

  const handleTestUltramsg = async () => {
    try {
      setUltramsgStatus("loading");
      const res = await axios.post(`${API}/settings/ultramsg/test`, {}, axiosConfig);
      setUltramsgStatus(res.data);
      if (res.data.success) toast.success(`Estado: ${res.data.status}`);
      else toast.error(res.data.error || "Error de conexión");
    } catch (error) {
      setUltramsgStatus({ success: false, error: error.message });
      toast.error("Error al verificar UltraMsg");
    }
  };

  // Activity events
  const [events, setEvents] = useState([]);

  // Migration
  const [migrationForm, setMigrationForm] = useState({
    old_base: "",
    old_user: "",
    old_pass: "",
  });
  const [migrationRunning, setMigrationRunning] = useState(false);
  const [migrationReport, setMigrationReport] = useState(null);

  const handleMigrate = async () => {
    if (!migrationForm.old_base || !migrationForm.old_user || !migrationForm.old_pass) {
      toast.error("Completá URL, usuario y contraseña de la instancia vieja");
      return;
    }
    // Safety check: prevent self-migration (source == destination)
    try {
      const selfHost = new URL(window.location.origin).hostname;
      const oldHost = new URL(migrationForm.old_base).hostname;
      if (selfHost === oldHost) {
        toast.error(
          "El URL de origen es el mismo que esta app. Usá la URL del preview (la que termina en .preview.emergentagent.com) o de otra instancia con los datos."
        );
        return;
      }
    } catch (e) {
      toast.error("URL inválida");
      return;
    }
    if (
      !window.confirm(
        "Esto copiará todos los clientes y vouchers de la instancia vieja a esta base de datos. Puede tardar varios minutos. ¿Continuar?"
      )
    )
      return;
    setMigrationRunning(true);
    setMigrationReport(null);
    try {
      const res = await axios.post(
        `${API}/admin/migrate-from-old`,
        migrationForm,
        { ...axiosConfig, timeout: 600000 }
      );
      setMigrationReport(res.data.report);
      toast.success("Migración completada");
      setMigrationForm((f) => ({ ...f, old_pass: "" }));
      loadClients();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error durante migración");
      setMigrationReport({ error: error.response?.data?.detail || error.message });
    } finally {
      setMigrationRunning(false);
    }
  };

  const loadEvents = async () => {
    try {
      const res = await axios.get(`${API}/events?limit=15`, axiosConfig);
      setEvents(res.data.events || []);
    } catch (error) {
      console.error("Error loading events");
    }
  };

  useEffect(() => {
    if (activeSection === "dashboard") loadEvents();
    if (activeSection === "recordatorios") loadReminderIssues();
    if (activeSection === "configuracion") {
      if (!emailTemplates) loadEmailTemplates();
      loadUltramsg();
    }
    if (activeSection === "vendedores") loadUsers();
  }, [activeSection]); // eslint-disable-line

  const loadReminderIssues = async () => {
    try {
      const res = await axios.get(`${API}/reminders/issues`, axiosConfig);
      setReminderIssues(Object.fromEntries((res.data.issues || []).map((i) => [i.trip_id, i.problems])));
    } catch (error) {
      console.error("Error loading reminder issues");
    }
  };

  const handleSectionClick = (key) => {
    if (key === "nuevo") {
      setNewClientDialog(true);
      setActiveSection("clientes");
      return;
    }
    setActiveSection(key);
  };

  const filteredClients = (() => {
    const q = searchQuery.trim().toLowerCase();
    return clients.filter((c) => {
      const haystack = [
        c.name, c.last_name, c.email, c.phone, c.phone_secondary,
        ...(c.extra_emails || []),
        c.client_number != null ? `#${c.client_number}` : "",
        ...(c.trips || []).map((t) => t.trip_destination),
      ].filter(Boolean).join(" ").toLowerCase();
      if (q && !haystack.includes(q)) return false;
      if (clientFilter === "reminder" && !c.reminder_enabled) return false;
      if (clientFilter === "no_vouchers" && (c.vouchers_count || 0) > 0) return false;
      if (clientFilter === "has_vouchers" && (c.vouchers_count || 0) === 0) return false;
      if (sellerFilter !== "all") {
        if (sellerFilter === "unassigned" && c.seller_id) return false;
        if (sellerFilter !== "unassigned" && c.seller_id !== sellerFilter) return false;
      }
      return true;
    });
  })();

  const listClients = filteredClients.filter((c) =>
    clientesTab === "history" ? c.status === "history" : clientesTab === "list" ? c.status !== "history" : true
  );

  const allTrips = clients.flatMap((c) => (c.trips || []).map((t) => ({ ...t, client: c })));

  const reminderClients = allTrips
    .filter((t) => t.status === "active" && t.reminder_enabled && !t.reminder_sent_at)
    .filter((t) => {
      if (reminderSellerFilter === "all") return true;
      if (reminderSellerFilter === "unassigned") return !t.client.seller_id;
      return t.client.seller_id === reminderSellerFilter;
    })
    .sort((a, b) => {
      const da = a.trip_date ? new Date(a.trip_date).getTime() : Infinity;
      const db_ = b.trip_date ? new Date(b.trip_date).getTime() : Infinity;
      return da - db_;
    });
  // ===========================================================

  return (
    <div className="min-h-screen flex bg-sky-gradient">
      {/* ==================== SIDEBAR ==================== */}
      <aside
        className="w-64 bg-gradient-to-b from-sky-950 to-sky-900 text-sky-100 flex flex-col shrink-0"
        style={{ minHeight: "100vh" }}
        data-testid="admin-sidebar"
      >
        <div className="p-5 border-b border-sky-800/60">
          <div className="flex items-center gap-3">
            {logo ? (
              <img
                src={logo}
                alt="Logo"
                className="h-12 w-12 object-contain rounded-lg bg-white/10 p-1"
              />
            ) : (
              <div className="bg-sky-500 p-2.5 rounded-xl shadow-md">
                <Plane className="w-6 h-6 text-white" />
              </div>
            )}
            <div className="min-w-0">
              <p
                className="font-bold font-heading text-white text-lg truncate"
                title={agencyName}
                data-testid="sidebar-agency-name"
              >
                {agencyName}
              </p>
              <p className="text-xs text-sky-300/70 uppercase tracking-wider">
                Backoffice
              </p>
            </div>
          </div>
        </div>

        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          <p className="text-[10px] uppercase tracking-wider text-sky-400/60 px-3 pt-2 pb-1">
            Menú Principal
          </p>
          {[
            { key: "dashboard", label: "Inicio", Icon: Home },
            { key: "nuevo", label: "Nuevo Cliente", Icon: UserPlus },
            { key: "clientes", label: "Clientes", Icon: Users },
            { key: "buscar", label: "Buscar", Icon: Search },
            { key: "recordatorios", label: "Recordatorios", Icon: Bell },
            { key: "vouchers", label: "Vouchers", Icon: Upload },
          ].map((item) => {
            const isActive = activeSection === item.key;
            const Icon = item.Icon;
            return (
              <button
                key={item.key}
                type="button"
                onClick={() => handleSectionClick(item.key)}
                data-testid={`nav-${item.key}`}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
                  isActive
                    ? "bg-sky-500 text-white shadow-lg shadow-sky-500/30"
                    : "text-sky-200/90 hover:bg-sky-800/60 hover:text-white"
                }`}
              >
                <Icon className="w-4 h-4 shrink-0" />
                <span className="truncate">{item.label}</span>
                {item.key === "recordatorios" && reminderClients.length > 0 && (
                  <span className="ml-auto text-xs bg-sky-400/30 text-white px-1.5 rounded-full">
                    {reminderClients.length}
                  </span>
                )}
              </button>
            );
          })}

          <p className="text-[10px] uppercase tracking-wider text-sky-400/60 px-3 pt-5 pb-1">
            Herramientas
          </p>
          {[
            { key: "plantillas", label: "Plantillas", Icon: Sparkles },
            ...((currentAdmin?.role || "admin") === "admin"
              ? [{ key: "vendedores", label: "Vendedores", Icon: UserCog }]
              : []),
            { key: "configuracion", label: "Configuración", Icon: Settings },
          ].map((item) => {
            const isActive = activeSection === item.key;
            const Icon = item.Icon;
            return (
              <button
                key={item.key}
                type="button"
                onClick={() => handleSectionClick(item.key)}
                data-testid={`nav-${item.key}`}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
                  isActive
                    ? "bg-sky-500 text-white shadow-lg shadow-sky-500/30"
                    : "text-sky-200/90 hover:bg-sky-800/60 hover:text-white"
                }`}
              >
                <Icon className="w-4 h-4 shrink-0" />
                <span className="truncate">{item.label}</span>
              </button>
            );
          })}
        </nav>

        <div className="p-3 border-t border-sky-800/60">
          <button
            type="button"
            onClick={onLogout}
            data-testid="logout-button"
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-sky-200/90 hover:bg-red-500/20 hover:text-red-300 transition-all"
          >
            <LogOut className="w-4 h-4 shrink-0" />
            Cerrar Sesión
          </button>
        </div>
      </aside>

      {/* ==================== MAIN ==================== */}
      <main className="flex-1 overflow-y-auto">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-8">
          {/* ---------- DASHBOARD ---------- */}
          {activeSection === "dashboard" && (() => {
            const now = new Date();
            const totalClients = clients.length;
            const totalVouchers = clients.reduce(
              (s, c) => s + (c.vouchers_count || 0),
              0
            );
            const activeReminders = reminderClients.length;
            const upcomingTrips = allTrips
              .filter((t) => t.trip_date && daysUntil(t.trip_date) >= 0)
              .sort(
                (a, b) => new Date(a.trip_date) - new Date(b.trip_date)
              )
              .slice(0, 5);
            const sentThisMonth = allTrips.filter((c) => {
              if (!c.reminder_sent_at) return false;
              const d = new Date(c.reminder_sent_at);
              return (
                d.getMonth() === now.getMonth() &&
                d.getFullYear() === now.getFullYear()
              );
            }).length;
            const stats = [
              {
                label: "Clientes activos",
                value: clients.filter((c) => c.status !== "history").length,
                Icon: Users,
                color: "from-sky-500 to-cyan-500",
              },
              {
                label: "Vouchers subidos",
                value: totalVouchers,
                Icon: FileText,
                color: "from-sky-600 to-blue-500",
              },
              {
                label: "Recordatorios pendientes",
                value: activeReminders,
                Icon: Bell,
                color: "from-cyan-500 to-sky-500",
              },
              {
                label: "Enviados este mes",
                value: sentThisMonth,
                Icon: TrendingUp,
                color: "from-sky-400 to-cyan-400",
              },
            ];
            return (
              <section data-testid="section-dashboard">
                <h1 className="text-3xl font-bold font-heading text-sky-900 mb-2">
                  Inicio
                </h1>
                <p className="text-sky-700/70 mb-6">
                  Resumen del estado de tu agencia · {totalClients} clientes en total.
                </p>
                {(currentAdmin?.role || "admin") === "admin" && (
                  <TripsMigrationCard api={API} axiosConfig={axiosConfig} onDone={loadClients} />
                )}

                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                  {stats.map((s) => {
                    const Icon = s.Icon;
                    return (
                      <Card
                        key={s.label}
                        className="glass-card border-0 overflow-hidden"
                        data-testid={`stat-${s.label}`}
                      >
                        <div className={`h-1 bg-gradient-to-r ${s.color}`} />
                        <CardContent className="pt-5">
                          <div className="flex items-center justify-between mb-2">
                            <div
                              className={`bg-gradient-to-br ${s.color} p-2 rounded-lg shadow-sm`}
                            >
                              <Icon className="w-4 h-4 text-white" />
                            </div>
                          </div>
                          <p className="text-3xl font-bold font-heading text-sky-900">
                            {s.value}
                          </p>
                          <p className="text-xs text-sky-700/70 mt-1">
                            {s.label}
                          </p>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                  <Card className="glass-card border-0">
                    <CardHeader>
                      <CardTitle className="text-lg font-heading text-sky-900 flex items-center gap-2">
                        <Plane className="w-5 h-5" />
                        Próximos viajes
                      </CardTitle>
                      <CardDescription>
                        Los 5 viajes con fecha más cercana
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      {upcomingTrips.length === 0 ? (
                        <p className="text-sm text-sky-700/60 py-4 text-center">
                          Sin viajes programados. Cargá fechas en el diálogo de
                          Recordatorio.
                        </p>
                      ) : (
                        <div className="space-y-2">
                          {upcomingTrips.map((c) => {
                            const daysLeft = daysUntil(c.trip_date);
                            return (
                              <button
                                key={c.id}
                                type="button"
                                onClick={() => openTripReminder(c.client, c)}
                                className="w-full text-left px-3 py-2 rounded-lg border border-sky-100 bg-white/70 hover:bg-sky-50 hover:border-sky-200 transition-all"
                                data-testid={`upcoming-trip-${c.id}`}
                              >
                                <div className="flex items-center justify-between gap-2">
                                  <div className="min-w-0">
                                    <p className="font-semibold text-sky-900 text-sm truncate">
                                      {c.client.name} {c.client.last_name || ""}
                                    </p>
                                    <p className="text-xs text-sky-700/70 truncate">
                                      {c.trip_destination || c.trip_type || "—"}
                                    </p>
                                  </div>
                                  <div className="text-right shrink-0">
                                    <p className="text-sm font-semibold text-sky-700">
                                      en {daysLeft}d
                                    </p>
                                    <p className="text-[10px] text-sky-600/70">
                                      {fmtDate(c.trip_date)}
                                    </p>
                                  </div>
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  <Card className="glass-card border-0">
                    <CardHeader>
                      <CardTitle className="text-lg font-heading text-sky-900 flex items-center gap-2">
                        <TrendingUp className="w-5 h-5" />
                        Acciones rápidas
                      </CardTitle>
                      <CardDescription>Atajos del día a día</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      <Button
                        onClick={() => setNewClientDialog(true)}
                        className="w-full justify-start btn-sky text-white"
                        data-testid="dashboard-new-client"
                      >
                        <UserPlus className="w-4 h-4 mr-2" />
                        Nuevo Cliente
                      </Button>
                      <Button
                        onClick={() => setActiveSection("recordatorios")}
                        variant="outline"
                        className="w-full justify-start border-sky-200 text-sky-700 hover:bg-sky-50"
                        data-testid="dashboard-go-reminders"
                      >
                        <Bell className="w-4 h-4 mr-2" />
                        Ver recordatorios ({activeReminders})
                      </Button>
                      <Button
                        onClick={() => setActiveSection("plantillas")}
                        variant="outline"
                        className="w-full justify-start border-sky-200 text-sky-700 hover:bg-sky-50"
                        data-testid="dashboard-go-templates"
                      >
                        <Sparkles className="w-4 h-4 mr-2" />
                        Editar plantillas
                      </Button>
                      <Button
                        onClick={() => setActiveSection("buscar")}
                        variant="outline"
                        className="w-full justify-start border-sky-200 text-sky-700 hover:bg-sky-50"
                        data-testid="dashboard-go-search"
                      >
                        <Search className="w-4 h-4 mr-2" />
                        Buscar cliente
                      </Button>
                    </CardContent>
                  </Card>
                </div>

                <ReturnsThisWeekCard trips={allTrips} onOpen={openClient} />

                {/* Seller ranking (admin only) */}
                <SellerRankingCard
                  users={users}
                  clients={clients}
                  currentAdmin={currentAdmin}
                  now={now}
                />

                {/* Activity feed */}
                <Card className="glass-card border-0 mt-5" data-testid="activity-card">
                  <CardHeader>
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <div>
                        <CardTitle className="text-lg font-heading text-sky-900 flex items-center gap-2">
                          <Bell className="w-5 h-5" />
                          Actividad reciente
                        </CardTitle>
                        <CardDescription>
                          Últimos envíos del sistema (scheduler, emails, WhatsApp)
                        </CardDescription>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={loadEvents}
                        className="border-sky-200 text-sky-700 hover:bg-sky-50"
                        data-testid="refresh-events-button"
                      >
                        <Loader2 className="w-4 h-4 mr-2" />
                        Actualizar
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {events.length === 0 ? (
                      <p className="text-sm text-sky-700/60 text-center py-6">
                        Aún no hay actividad registrada. Cuando envíes emails o
                        WhatsApps, vas a verlos acá.
                      </p>
                    ) : (
                      <div className="space-y-2 max-h-96 overflow-y-auto pr-1">
                        {events.map((ev) => {
                          const success = ev.success !== false;
                          const channelIcon =
                            ev.channel === "whatsapp"
                              ? "📱"
                              : ev.channel === "email"
                              ? "📧"
                              : "•";
                          const typeLabel =
                            ev.type === "reminder_sent"
                              ? "Recordatorio enviado"
                              : ev.type === "reminder_failed"
                              ? "Recordatorio falló"
                              : ev.type === "reminder_skipped"
                              ? "Recordatorio omitido"
                              : ev.type === "voucher_email_sent"
                              ? "Email de vouchers enviado"
                              : ev.type === "post_trip_sent"
                              ? "Encuesta post viaje enviada"
                              : ev.type === "post_trip_failed"
                              ? "Encuesta post viaje falló"
                              : ev.type;
                          const ts = ev.created_at
                            ? new Date(ev.created_at).toLocaleString()
                            : "";
                          return (
                            <div
                              key={ev.id}
                              className={`flex items-start gap-3 px-3 py-2 rounded-lg border ${
                                success
                                  ? "bg-white/70 border-sky-100"
                                  : "bg-red-50/60 border-red-200"
                              }`}
                              data-testid={`event-${ev.id}`}
                            >
                              <div className="text-lg shrink-0 pt-0.5">
                                {success ? "✓" : "✗"}
                              </div>
                              <div className="min-w-0 flex-1">
                                <p
                                  className={`text-sm font-medium ${
                                    success ? "text-sky-900" : "text-red-800"
                                  }`}
                                >
                                  {channelIcon} {typeLabel}
                                  {ev.client_name && (
                                    <span className="font-normal text-sky-700/80">
                                      {" "}
                                      — {ev.client_name}
                                    </span>
                                  )}
                                </p>
                                {ev.detail && (
                                  <p
                                    className="text-xs text-sky-700/70 truncate"
                                    title={ev.detail}
                                  >
                                    {ev.detail}
                                  </p>
                                )}
                                <p className="text-[10px] text-sky-600/60 mt-0.5">
                                  {ts}
                                </p>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </section>
            );
          })()}

          {/* ---------- CLIENTES ---------- */}
          {activeSection === "clientes" && (
            <section data-testid="section-clientes">
              <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
                <div>
                  <h1 className="text-3xl font-bold font-heading text-sky-900">
                    Gestión de Clientes
                  </h1>
                  <p className="text-sky-700/70 mt-1">
                    Administra tus clientes y envía sus vouchers por email
                  </p>
                </div>
                <div className="flex gap-2 flex-wrap">
                  {clientesTab === "database" && (
                    <Button
                      variant="outline"
                      onClick={exportClientsCSV}
                      className="border-sky-200 text-sky-700 hover:bg-sky-50"
                      data-testid="export-csv-button"
                    >
                      <FileText className="w-4 h-4 mr-2" />
                      Exportar a Excel/CSV
                    </Button>
                  )}
                  <Button
                    onClick={() => setNewClientDialog(true)}
                    className="btn-sky text-white"
                    data-testid="add-client-button"
                  >
                    <UserPlus className="w-4 h-4 mr-2" />
                    Nuevo Cliente
                  </Button>
                </div>
              </div>

              <div className="flex gap-2 mb-4 border-b border-sky-200">
                {[
                  { k: "list", label: `Activos (${clients.filter((c) => c.status !== "history").length})` },
                  { k: "history", label: `Historial (${clients.filter((c) => c.status === "history").length})` },
                  { k: "database", label: "Base de datos" },
                ].map((t) => (
                  <button
                    key={t.k}
                    type="button"
                    onClick={() => setClientesTab(t.k)}
                    data-testid={`clientes-tab-${t.k}`}
                    className={`px-4 py-2 text-sm font-medium transition-all border-b-2 -mb-px ${
                      clientesTab === t.k
                        ? "border-sky-600 text-sky-900"
                        : "border-transparent text-sky-700/70 hover:text-sky-900"
                    }`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>

              {(currentAdmin?.role || "admin") === "admin" && (
                <TripsMigrationCard api={API} axiosConfig={axiosConfig} onDone={loadClients} />
              )}

              {clientesTab === "database" && (
                <Card className="glass-card border-0 mb-4" data-testid="database-table-card">
                  <CardContent className="pt-6">
                    <div className="flex flex-col sm:flex-row gap-3 mb-4">
                      <div className="relative flex-1">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-sky-400" />
                        <Input
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          placeholder="Buscar por nombre, email o teléfono..."
                          className="pl-9 border-sky-200"
                          data-testid="database-search-input"
                        />
                      </div>
                      {(currentAdmin?.role || "admin") === "admin" && (
                        <Select value={sellerFilter} onValueChange={setSellerFilter}>
                          <SelectTrigger
                            className="border-sky-200 sm:w-56"
                            data-testid="db-seller-filter"
                          >
                            <SelectValue placeholder="Filtrar por vendedor" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">Todos los vendedores</SelectItem>
                            <SelectItem value="unassigned">Sin asignar</SelectItem>
                            {users.map((u) => (
                              <SelectItem key={u.id} value={u.id}>
                                {u.full_name || u.username}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-sky-200 text-sky-900">
                            <th className="text-left py-2 px-3">Nº</th>
                            <th className="text-left py-2 px-3">Nombre</th>
                            <th className="text-left py-2 px-3">Apellido</th>
                            <th className="text-left py-2 px-3">Email</th>
                            <th className="text-left py-2 px-3">Teléfono</th>
                            <th className="text-left py-2 px-3">Viajes</th>
                            {(currentAdmin?.role || "admin") === "admin" && (
                              <th className="text-left py-2 px-3">Vendedor</th>
                            )}
                            <th className="text-left py-2 px-3">Acciones</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredClients.length === 0 ? (
                            <tr>
                              <td colSpan={(currentAdmin?.role || "admin") === "admin" ? 8 : 7} className="py-8 text-center text-sky-700/60">
                                Sin clientes.
                              </td>
                            </tr>
                          ) : (
                            filteredClients.map((c) => (
                              <tr
                                key={c.id}
                                className="border-b border-sky-100 hover:bg-sky-50/50"
                                data-testid={`db-row-${c.id}`}
                              >
                                <td className="py-2 px-3 font-mono text-xs text-sky-700">
                                  {c.client_number != null ? `#${c.client_number}` : "-"}
                                </td>
                                <td className="py-2 px-3">{c.name}</td>
                                <td className="py-2 px-3">{c.last_name || "-"}</td>
                                <td className="py-2 px-3 text-sky-700">{c.email}</td>
                                <td className="py-2 px-3">{c.phone || "-"}</td>
                                <td className="py-2 px-3 text-xs">
                                  {(c.trips || []).length}
                                  {c.status === "history" && (
                                    <span className="ml-1 bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">historial</span>
                                  )}
                                </td>
                                {(currentAdmin?.role || "admin") === "admin" && (
                                  <td className="py-2 px-3">
                                    <Select
                                      value={c.seller_id || "unassigned"}
                                      onValueChange={(v) => handleAssignSeller(c.id, v)}
                                    >
                                      <SelectTrigger
                                        className="border-sky-200 h-8 text-xs"
                                        data-testid={`db-assign-seller-${c.id}`}
                                      >
                                        <SelectValue />
                                      </SelectTrigger>
                                      <SelectContent>
                                        <SelectItem value="unassigned">Sin asignar</SelectItem>
                                        {users.map((u) => (
                                          <SelectItem key={u.id} value={u.id}>
                                            {u.full_name || u.username}
                                          </SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                  </td>
                                )}
                                <td className="py-2 px-3">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => openEditClient(c)}
                                    className="border-sky-200 text-sky-700 hover:bg-sky-50 h-7 px-2 text-xs"
                                    data-testid={`db-edit-${c.id}`}
                                  >
                                    Editar
                                  </Button>
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                    <p className="text-xs text-sky-700/70 mt-3">
                      Mostrando {filteredClients.length} de {clients.length} clientes.
                    </p>
                  </CardContent>
                </Card>
              )}

              {(clientesTab === "list" || clientesTab === "history") && (
                <Card className="glass-card border-0">
                <CardContent className="pt-6">
                  <div className="flex flex-col sm:flex-row gap-3 mb-5">
                    <div className="relative flex-1">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-sky-400" />
                      <Input
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Buscar por nombre, email, teléfono o destino..."
                        className="pl-9 border-sky-200"
                        data-testid="client-search-input"
                      />
                      {searchQuery && (
                        <button
                          type="button"
                          onClick={() => setSearchQuery("")}
                          className="absolute right-2 top-1/2 -translate-y-1/2 text-sky-400 hover:text-sky-700 p-1"
                          data-testid="clear-search-button"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                    <div className="flex gap-2 flex-wrap items-center">
                      <Filter className="w-4 h-4 text-sky-600 shrink-0" />
                      {[
                        { value: "all", label: "Todos" },
                        { value: "reminder", label: "Con recordatorio" },
                        { value: "no_vouchers", label: "Sin vouchers" },
                        { value: "has_vouchers", label: "Con vouchers" },
                      ].map((f) => (
                        <button
                          key={f.value}
                          type="button"
                          onClick={() => setClientFilter(f.value)}
                          data-testid={`filter-${f.value}`}
                          className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                            clientFilter === f.value
                              ? "bg-sky-600 text-white shadow-sm"
                              : "bg-sky-50 text-sky-700 hover:bg-sky-100"
                          }`}
                        >
                          {f.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-3">
                    {clients.length === 0 ? (
                      <p className="text-center text-sky-700/60 py-12">
                        No hay clientes registrados. Crea el primero.
                      </p>
                    ) : listClients.length === 0 ? (
                      <p
                        className="text-center text-sky-700/60 py-12"
                        data-testid="no-results"
                      >
                        {clientesTab === "history"
                          ? "No hay clientes en el historial para los filtros actuales."
                          : "Sin resultados para los filtros actuales."}
                      </p>
                    ) : (
                      listClients.map((client) => (
                        <div
                          key={client.id}
                          className="border border-sky-100 rounded-xl p-4 hover:shadow-lg hover:border-sky-200 transition-all bg-white/80"
                          data-testid={`client-card-${client.id}`}
                        >
                          <div className="flex justify-between items-start gap-3 flex-wrap">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                {client.client_number != null && (
                                  <span className="text-xs font-mono bg-sky-900 text-sky-100 px-2 py-0.5 rounded">
                                    #{client.client_number}
                                  </span>
                                )}
                                <h3 className="font-semibold text-lg text-sky-900 font-heading">
                                  {client.name}
                                  {client.last_name ? ` ${client.last_name}` : ""}
                                </h3>
                                {client.vouchers_count > 0 && (
                                  <span className="text-xs bg-sky-100 text-sky-700 px-2 py-0.5 rounded-full">
                                    {client.vouchers_count} voucher
                                    {client.vouchers_count !== 1 ? "s" : ""}
                                  </span>
                                )}
                              </div>
                              <p className="text-sm text-sky-700/80">
                                {client.email}
                              </p>
                              {client.current_trip_id && (
                                <p className="text-sm text-sky-800 mt-1 flex items-center gap-1 flex-wrap" data-testid={`client-trip-summary-${client.id}`}>
                                  <span className="font-medium">{TRIP_TYPE_EMOJI[client.trip_type] || "🧳"} {tripTitle(client)}</span>
                                  <span className="text-sky-600/80 text-xs">· {tripDatesLabel(client)}</span>
                                  {client.trips.length > 1 && (
                                    <span className="text-xs bg-sky-900 text-sky-100 px-2 py-0.5 rounded-full">
                                      {client.trips.length} viajes
                                    </span>
                                  )}
                                </p>
                              )}
                              <p className="text-xs text-sky-600/60 mt-1 truncate">
                                Link: {window.location.origin}/client/
                                {client.access_token}
                              </p>
                              {client.notes && (
                                <p className="text-xs text-sky-700 mt-1 flex items-center">
                                  <FileText className="w-3 h-3 inline mr-1" />
                                  Tiene notas
                                </p>
                              )}
                              {client.reminder_enabled && (
                                <p className="text-xs text-sky-700 mt-1 flex items-center">
                                  <Bell className="w-3 h-3 inline mr-1" />
                                  Recordatorio
                                  {client.trip_date
                                    ? ` (${fmtDate(client.trip_date)})`
                                    : " activo"}
                                </p>
                              )}
                            </div>
                            <div className="flex gap-2 flex-wrap">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => openEditClient(client)}
                                data-testid={`edit-client-button-${client.id}`}
                                className="border-sky-200 text-sky-700 hover:bg-sky-50"
                                title="Editar datos"
                              >
                                Editar
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() =>
                                  copyClientLink(client.access_token)
                                }
                                data-testid={`copy-link-button-${client.id}`}
                                className="border-sky-200 text-sky-700 hover:bg-sky-50"
                              >
                                <Copy className="w-4 h-4" />
                              </Button>
                              <Button
                                size="sm"
                                onClick={() => handleSendEmail(client.id, client.current_trip_id)}
                                disabled={loading}
                                data-testid={`send-email-button-${client.id}`}
                                className="btn-sky text-white"
                              >
                                <Mail className="w-4 h-4 mr-1" />
                                Enviar Email
                              </Button>
                              {client.phone && (
                                <Button
                                  size="sm"
                                  onClick={() => {
                                    const phone = client.phone.replace(/\D/g, "");
                                    const link = `${window.location.origin}/client/${client.access_token}`;
                                    const text = encodeURIComponent(
                                      `Hola ${client.name}, ya están listos tus vouchers de viaje. Podés acceder acá: ${link}`
                                    );
                                    window.open(
                                      `https://wa.me/${phone}?text=${text}`,
                                      "_blank",
                                      "noopener,noreferrer"
                                    );
                                  }}
                                  data-testid={`whatsapp-button-${client.id}`}
                                  className="bg-green-500 hover:bg-green-600 text-white"
                                >
                                  <MessageCircle className="w-4 h-4" />
                                </Button>
                              )}
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => openClient(client)}
                                data-testid={`manage-vouchers-button-${client.id}`}
                                className="border-sky-200 text-sky-700 hover:bg-sky-50"
                              >
                                <Upload className="w-4 h-4 mr-1" />
                                Ficha y vouchers
                              </Button>
                              <Button
                                size="sm"
                                variant="destructive"
                                onClick={() => handleDeleteClient(client.id)}
                                data-testid={`delete-client-button-${client.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </CardContent>
              </Card>
              )}
            </section>
          )}

          {/* ---------- BUSCAR ---------- */}
          {activeSection === "buscar" && (
            <section data-testid="section-buscar">
              <h1 className="text-3xl font-bold font-heading text-sky-900 mb-2">
                Buscar Cliente
              </h1>
              <p className="text-sky-700/70 mb-6">
                Busca en todos los clientes, activos y del historial: nombre, email, teléfono o destino.
              </p>
              <Card className="glass-card border-0">
                <CardContent className="pt-6">
                  <div className="relative mb-5">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-sky-400" />
                    <Input
                      autoFocus
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Escribí un nombre, email, teléfono o destino..."
                      className="pl-11 border-sky-200 h-12 text-base"
                      data-testid="search-focused-input"
                    />
                  </div>
                  <div className="space-y-3">
                    {searchQuery.trim() === "" ? (
                      <p className="text-center text-sky-700/50 py-8">
                        Empezá a escribir para ver resultados.
                      </p>
                    ) : filteredClients.length === 0 ? (
                      <p className="text-center text-sky-700/60 py-8">
                        Sin resultados para "{searchQuery}".
                      </p>
                    ) : (
                      filteredClients.slice(0, 20).map((client) => (
                        <button
                          key={client.id}
                          type="button"
                          onClick={() => openClient(client)}
                          className="w-full text-left border border-sky-100 rounded-xl p-4 bg-white/80 hover:shadow-md hover:border-sky-300 transition-all"
                          data-testid={`search-result-${client.id}`}
                        >
                          <div className="flex items-center justify-between gap-3">
                            <div className="min-w-0">
                              <h3 className="font-semibold text-sky-900 font-heading flex items-center gap-2">
                                {client.name} {client.last_name || ""}
                                <span
                                  className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${
                                    client.status === "history" ? "bg-slate-100 text-slate-600" : "bg-emerald-50 text-emerald-700"
                                  }`}
                                  data-testid={`search-result-status-${client.id}`}
                                >
                                  {client.status === "history" ? "Historial" : "Activo"}
                                </span>
                              </h3>
                              <p className="text-sm text-sky-700/80 truncate">
                                {client.email}
                                {(client.trips || []).length > 0 &&
                                  ` · ${client.trips.map((t) => tripTitle(t)).join(", ")}`}
                              </p>
                            </div>
                            <Upload className="w-4 h-4 text-sky-400 shrink-0" />
                          </div>
                        </button>
                      ))
                    )}
                  </div>
                </CardContent>
              </Card>
            </section>
          )}

          {/* ---------- RECORDATORIOS ---------- */}
          {activeSection === "recordatorios" && (
            <section data-testid="section-recordatorios">
              <h1 className="text-3xl font-bold font-heading text-sky-900 mb-2">
                Recordatorios Pre-Viaje
              </h1>
              <p className="text-sky-700/70 mb-6">
                Viajes activos con recordatorio pendiente, ordenados por fecha de salida.
              </p>
              <Card className="glass-card border-0">
                <CardContent className="pt-6">
                  {(currentAdmin?.role || "admin") === "admin" && users.length > 0 && (
                    <div className="mb-4 flex items-center gap-2 flex-wrap">
                      <Label className="text-sky-900 font-medium text-sm">Filtrar por vendedor:</Label>
                      <Select value={reminderSellerFilter} onValueChange={setReminderSellerFilter}>
                        <SelectTrigger
                          className="border-sky-200 h-9 w-56"
                          data-testid="reminder-seller-filter"
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Todos los vendedores</SelectItem>
                          <SelectItem value="unassigned">Sin asignar</SelectItem>
                          {users.map((u) => (
                            <SelectItem key={u.id} value={u.id}>
                              {u.full_name || u.username}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {reminderSellerFilter !== "all" && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setReminderSellerFilter("all")}
                          className="border-sky-200 text-sky-700 hover:bg-sky-50 h-9"
                          data-testid="reminder-seller-filter-clear"
                        >
                          Limpiar
                        </Button>
                      )}
                    </div>
                  )}
                  {reminderClients.some((t) => reminderIssues[t.id]) && (
                    <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 flex gap-2" data-testid="reminder-issues-banner">
                      <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                      {reminderClients.filter((t) => reminderIssues[t.id]).length} recordatorio(s) tienen variables sin reemplazar y no se van a enviar hasta que los corrijas.
                    </div>
                  )}
                  {reminderClients.length === 0 ? (
                    <p className="text-center text-sky-700/60 py-12">
                      Ningún cliente tiene recordatorio activado todavía. Activalo
                      desde la ficha de un cliente.
                    </p>
                  ) : (
                    <div className="space-y-3">
                      {reminderClients.map((client) => (
                        <div
                          key={client.id}
                          className="border border-sky-100 rounded-xl p-4 bg-white/80 hover:shadow-md transition-all"
                          data-testid={`reminder-item-${client.id}`}
                        >
                          <div className="flex justify-between items-start gap-3 flex-wrap">
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <Bell className="w-4 h-4 text-sky-600" />
                                <h3 className="font-semibold text-sky-900 font-heading">
                                  {client.client.name} {client.client.last_name || ""}
                                </h3>
                                {client.trip_type && (
                                  <span className="text-xs">{TRIP_TYPE_EMOJI[client.trip_type]}</span>
                                )}
                              </div>
                              <p className="text-sm text-sky-700/80 mt-0.5">
                                {client.trip_destination || "Sin destino"} ·{" "}
                                {client.trip_date ? fmtDate(client.trip_date) : "Sin fecha"}
                                {" · "}
                                Aviso {client.reminder_days_before || 7} días antes
                                {" · "}
                                <span className="font-medium">
                                  {client.reminder_channel === "whatsapp"
                                    ? "📱 WhatsApp"
                                    : client.reminder_channel === "both"
                                    ? "📧📱 Ambos"
                                    : "📧 Email"}
                                </span>
                              </p>
                              {reminderIssues[client.id] && (
                                <p className="text-xs text-red-600 mt-1 flex items-center gap-1" data-testid={`reminder-issue-${client.id}`}>
                                  <AlertTriangle className="w-3.5 h-3.5" />
                                  {reminderIssues[client.id].join("; ")}
                                </p>
                              )}
                              {client.reminder_sent_at && (
                                <p className="text-xs text-green-700 mt-1">
                                  ✓ Enviado{" "}
                                  {new Date(
                                    client.reminder_sent_at
                                  ).toLocaleString()}
                                </p>
                              )}
                            </div>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => openTripReminder(client.client, client)}
                              className="border-sky-200 text-sky-700 hover:bg-sky-50"
                              data-testid={`edit-reminder-${client.id}`}
                            >
                              <Bell className="w-4 h-4 mr-1" />
                              Editar
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </section>
          )}

          {/* ---------- VOUCHERS ---------- */}
          {activeSection === "vouchers" && selectedClient && (
            <section data-testid="section-vouchers">
              <div className="flex justify-between items-start mb-6 flex-wrap gap-3">
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    {selectedClient.client_number != null && (
                      <span className="text-xs font-mono bg-sky-900 text-sky-100 px-2 py-0.5 rounded">
                        #{selectedClient.client_number}
                      </span>
                    )}
                    <h1 className="text-3xl font-bold font-heading text-sky-900" data-testid="client-file-name">
                      {selectedClient.name} {selectedClient.last_name || ""}
                    </h1>
                  </div>
                  <p className="text-sky-700/70 mt-1">
                    {selectedClient.email}
                    {selectedClient.phone ? ` · ${selectedClient.phone}` : ""}
                    {selectedClient.seller_name ? ` · Vendedor: ${selectedClient.seller_name}` : ""}
                  </p>
                </div>
                <div className="flex gap-2 flex-wrap">
                  <Button
                    variant="outline"
                    onClick={() => openEditClient(selectedClient)}
                    data-testid="client-file-edit-button"
                    className="border-sky-200 text-sky-700 hover:bg-sky-50"
                  >
                    <Pencil className="w-4 h-4 mr-2" />
                    Editar datos
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setNotesDialog(true)}
                    data-testid="notes-button"
                    className="border-sky-200 text-sky-700 hover:bg-sky-50"
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    Notas
                  </Button>
                </div>
              </div>

              <TripsPanel
                trips={selectedClient.trips || []}
                selectedTripId={selectedTrip?.id}
                onSelect={setSelectedTrip}
                onNew={() => setTripDialog({ open: true, trip: null })}
                onEdit={(t) => setTripDialog({ open: true, trip: t })}
                onDelete={handleDeleteTrip}
              />

              {selectedTrip && (
                <div className="flex justify-between items-center mb-4 flex-wrap gap-3 rounded-xl bg-white/70 border border-sky-100 px-4 py-3" data-testid="selected-trip-bar">
                  <div className="min-w-0">
                    <p className="text-xs uppercase tracking-wider text-sky-600/70">Vouchers del viaje</p>
                    <p className="font-semibold font-heading text-sky-900" data-testid="selected-trip-title">
                      {TRIP_TYPE_EMOJI[selectedTrip.trip_type] || "🧳"} {tripTitle(selectedTrip)}
                      <span className="font-normal text-sm text-sky-700/70"> · {tripDatesLabel(selectedTrip)}</span>
                    </p>
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => copyClientLink(selectedTrip.access_token)}
                      data-testid="trip-copy-link-button"
                      className="border-sky-200 text-sky-700 hover:bg-sky-50"
                    >
                      <Copy className="w-4 h-4 mr-1" />
                      Copiar link
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleSendEmail(selectedClient.id, selectedTrip.id)}
                      disabled={loading}
                      data-testid="trip-send-email-button"
                      className="border-sky-200 text-sky-700 hover:bg-sky-50"
                    >
                      <Mail className="w-4 h-4 mr-1" />
                      Enviar link
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setReminderDialog(true)}
                      data-testid="reminder-button"
                      className="border-sky-200 text-sky-700 hover:bg-sky-50"
                    >
                      <Bell className="w-4 h-4 mr-1" />
                      Recordatorio
                    </Button>
                    <Button
                      size="sm"
                      onClick={openVoucherDialog}
                      data-testid="upload-voucher-button"
                      className="btn-sky text-white"
                    >
                      <UploadCloud className="w-4 h-4 mr-1" />
                      Subir Vouchers
                    </Button>
                  </div>
                </div>
              )}

              {selectedTrip && (
                <PostTripBar trip={selectedTrip} client={selectedClient} api={API} axiosConfig={axiosConfig} onChanged={loadClients} />
              )}

              {selectedTrip && (
              <Card className="glass-card border-0">
                <CardContent className="pt-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {vouchers.length === 0 ? (
                      <p className="col-span-full text-center text-sky-700/60 py-12">
                        No hay vouchers para este viaje. Subí el primero arriba.
                      </p>
                    ) : (
                      vouchers.map((voucher, idx) => (
                        <div
                          key={voucher.id}
                          draggable
                          onDragStart={() => handleVoucherDragStart(idx)}
                          onDragOver={handleVoucherDragOver}
                          onDrop={() => handleVoucherDrop(idx)}
                          className={`border border-sky-100 rounded-xl p-4 hover:shadow-lg hover:border-sky-200 transition-all bg-white/80 cursor-move ${
                            voucherDragIdx === idx ? "opacity-40" : ""
                          }`}
                          data-testid={`voucher-card-${voucher.id}`}
                        >
                          <div className="flex items-start justify-between mb-2 gap-2">
                            <div className="flex items-start gap-2 flex-1 min-w-0">
                              <span className="text-sky-400 text-lg leading-none pt-1 select-none" title="Arrastrá para reordenar">⋮⋮</span>
                              <div className="flex-1 min-w-0">
                                <h4 className="font-semibold text-sky-900 font-heading flex items-center gap-1.5">
                                  <VoucherTypeIcon icon={voucherTypes.byKey[voucher.type]?.icon} className="w-4 h-4 text-sky-600" />
                                  {getVoucherTypeLabel(voucher.type)}
                                </h4>
                                <p
                                  className="text-xs text-sky-600/70 truncate"
                                  title={voucher.original_filename}
                                >
                                  {voucher.original_filename}
                                </p>
                              </div>
                            </div>
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => handleDeleteVoucher(voucher.id)}
                              data-testid={`delete-voucher-button-${voucher.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </CardContent>
              </Card>
              )}
            </section>
          )}

          {activeSection === "vouchers" && !selectedClient && (
            <section data-testid="section-vouchers-empty">
              <Card className="glass-card border-0">
                <CardContent className="py-16 text-center">
                  <Upload className="w-16 h-16 mx-auto text-sky-300 mb-4" />
                  <h3 className="text-xl font-semibold font-heading text-sky-900 mb-2">
                    Seleccioná un cliente primero
                  </h3>
                  <p className="text-sky-700/70 mb-4">
                    Abrí la sección "Clientes" y hacé click en "Vouchers" en la
                    ficha de un cliente.
                  </p>
                  <Button
                    onClick={() => setActiveSection("clientes")}
                    className="btn-sky text-white"
                  >
                    <Users className="w-4 h-4 mr-2" />
                    Ir a Clientes
                  </Button>
                </CardContent>
              </Card>
            </section>
          )}

          {/* ---------- PLANTILLAS ---------- */}
          {activeSection === "plantillas" && (
            <section data-testid="section-plantillas">
              <h1 className="text-3xl font-bold font-heading text-sky-900 mb-2">
                Plantillas
              </h1>
              <p className="text-sky-700/70 mb-6">
                Un solo sistema para todos los envíos (manuales y automáticos, por email y WhatsApp).
              </p>
              {(
                <>
                  <GeneralTemplateCard api={API} axiosConfig={axiosConfig} />

                  {/* Destination templates section */}
                  <div className="mt-10">
                    <h2 className="text-2xl font-bold font-heading text-sky-900 mb-2">
                      Plantillas por Destino
                    </h2>
                    <p className="text-sky-700/70 mb-4 text-sm">
                      Plantillas por destino (ej. Cancún, Punta Cana) o generales (ej. Vuelo, Paquete). Se eligen en el recordatorio de cada viaje; las de destino además completan el destino.
                    </p>

                    <Card className="glass-card border-0 mb-5" data-testid="destination-template-form-card">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-lg font-heading text-sky-900">
                          {destTemplateForm.id ? "Editar plantilla" : "Nueva plantilla"}
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        <div className="flex gap-2" data-testid="dest-template-kind">
                          {[
                            { k: "destination", label: "Por destino" },
                            { k: "general", label: "General (sin destino)" },
                          ].map((o) => (
                            <button
                              key={o.k}
                              type="button"
                              onClick={() => setDestTemplateForm((f) => ({ ...f, kind: o.k }))}
                              className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
                                destTemplateForm.kind === o.k
                                  ? "bg-sky-600 border-sky-600 text-white"
                                  : "bg-white border-sky-200 text-sky-700 hover:bg-sky-50"
                              }`}
                              data-testid={`dest-template-kind-${o.k}`}
                            >
                              {o.label}
                            </button>
                          ))}
                        </div>
                        <div>
                          <Label className="text-sky-900 font-medium">
                            {destTemplateForm.kind === "general" ? "Nombre" : "Destino"}
                          </Label>
                          <Input
                            value={destTemplateForm.kind === "general" ? destTemplateForm.name : destTemplateForm.destination}
                            onChange={(e) =>
                              setDestTemplateForm((f) => ({
                                ...f,
                                [f.kind === "general" ? "name" : "destination"]: e.target.value,
                              }))
                            }
                            placeholder={destTemplateForm.kind === "general" ? "Ej: Vuelo, Escapada corta" : "Ej: Cancún, París, Bariloche"}
                            className="border-sky-200"
                            data-testid="dest-template-destination-input"
                          />
                        </div>
                        <div>
                          <Label className="text-sky-900 font-medium">Mensaje</Label>
                          <RichTextEditor
                            value={destTemplateForm.message}
                            onChange={(v) => setDestTemplateForm((f) => ({ ...f, message: v }))}
                            axiosConfig={axiosConfig}
                            previewKind="pre_trip"
                            testId="dest-template-editor"
                          />
                          <div className="mt-2"><TemplateVarsHint compact /></div>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            onClick={handleSaveDestTemplate}
                            className="btn-sky text-white"
                            data-testid="save-dest-template-button"
                          >
                            <CheckCircle2 className="w-4 h-4 mr-2" />
                            {destTemplateForm.id ? "Guardar cambios" : "Crear plantilla"}
                          </Button>
                          {destTemplateForm.id && (
                            <Button
                              onClick={() => setDestTemplateForm(EMPTY_DEST_FORM)}
                              variant="outline"
                              className="border-sky-200 text-sky-700 hover:bg-sky-50"
                              data-testid="cancel-dest-template-button"
                            >
                              Cancelar
                            </Button>
                          )}
                        </div>
                      </CardContent>
                    </Card>

                    {destTemplates.length === 0 ? (
                      <p className="text-center text-sky-700/60 py-6 text-sm">
                        Todavía no hay plantillas. Creá la primera arriba.
                      </p>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {destTemplates.map((dt) => (
                          <Card
                            key={dt.id}
                            className="glass-card border-0"
                            data-testid={`dest-template-${dt.id}`}
                          >
                            <CardHeader className="pb-2">
                              <div className="flex items-center justify-between gap-2">
                                <CardTitle className="text-base font-heading text-sky-900 flex items-center gap-2">
                                  {templateLabel(dt)}
                                  <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${dt.kind === "general" ? "bg-amber-50 text-amber-700" : "bg-sky-50 text-sky-700"}`}>
                                    {dt.kind === "general" ? "General" : "Destino"}
                                  </span>
                                </CardTitle>
                                <div className="flex gap-1">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() =>
                                      setDestTemplateForm({
                                        id: dt.id,
                                        kind: dt.kind || "destination",
                                        name: dt.name || "",
                                        destination: dt.destination || "",
                                        message: dt.message,
                                      })
                                    }
                                    className="border-sky-200 text-sky-700 hover:bg-sky-50 h-7 px-2 text-xs"
                                    data-testid={`edit-dest-template-${dt.id}`}
                                  >
                                    Editar
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="destructive"
                                    onClick={() => handleDeleteDestTemplate(dt.id)}
                                    className="h-7 px-2 text-xs"
                                    data-testid={`delete-dest-template-${dt.id}`}
                                  >
                                    <Trash2 className="w-3 h-3" />
                                  </Button>
                                </div>
                              </div>
                            </CardHeader>
                            <CardContent>
                              <div className="text-xs text-sky-700/80 rte-content line-clamp-6" dangerouslySetInnerHTML={{ __html: /<[a-z]/i.test(dt.message) ? dt.message : dt.message.replace(/</g, "&lt;").replace(/\n/g, "<br>") }} />
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    )}
                  </div>
                  <PostTripTemplateCard api={API} axiosConfig={axiosConfig} />
                </>
              )}
            </section>
          )}

          {/* ---------- CONFIGURACIÓN ---------- */}
          {activeSection === "configuracion" && (
            <section data-testid="section-configuracion">
              <h1 className="text-3xl font-bold font-heading text-sky-900 mb-2">
                Configuración
              </h1>
              <p className="text-sky-700/70 mb-6">
                Agencia, plantillas de email e integración WhatsApp.
              </p>
              {/* Sub-tabs */}
              <div className="flex gap-2 mb-6 flex-wrap border-b border-sky-200">
                {[
                  { k: "agencia", label: "Agencia" },
                  { k: "emails", label: "Emails" },
                  { k: "whatsapp", label: "WhatsApp (UltraMsg)" },
                  { k: "tipos", label: "Tipos de servicio" },
                  { k: "migrar", label: "Migrar datos" },
                ].map((t) => (
                  <button
                    key={t.k}
                    type="button"
                    onClick={() => setConfigTab(t.k)}
                    data-testid={`config-tab-${t.k}`}
                    className={`px-4 py-2 text-sm font-medium transition-all border-b-2 -mb-px ${
                      configTab === t.k
                        ? "border-sky-600 text-sky-900"
                        : "border-transparent text-sky-700/70 hover:text-sky-900"
                    }`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>

              {/* Agencia sub-section */}
              {configTab === "agencia" && (
                <>
                  <Card className="glass-card border-0 mb-5">
                    <CardHeader>
                      <CardTitle className="text-lg font-heading text-sky-900">
                        Logo de Agencia
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="flex items-center gap-5 flex-wrap">
                        {logo ? (
                          <img
                            src={logo}
                            alt="Logo actual"
                            className="h-24 w-24 object-contain bg-sky-50 rounded-xl border border-sky-200 p-2"
                          />
                        ) : (
                          <div className="h-24 w-24 bg-sky-50 rounded-xl border border-dashed border-sky-300 flex items-center justify-center">
                            <Plane className="w-10 h-10 text-sky-400" />
                          </div>
                        )}
                        <div className="flex-1 min-w-[200px]">
                          <Input
                            type="file"
                            accept="image/*"
                            onChange={handleUploadLogo}
                            disabled={uploadingLogo}
                            data-testid="logo-upload-input"
                            className="border-sky-200"
                          />
                          <p className="text-xs text-sky-600/70 mt-2">
                            {uploadingLogo
                              ? "Subiendo logo..."
                              : "JPG, PNG, SVG · máx 5MB"}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="glass-card border-0">
                    <CardHeader>
                      <CardTitle className="text-lg font-heading text-sky-900">
                        Nombre de Agencia
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <form
                        onSubmit={handleUpdateAgencyName}
                        className="flex gap-3 flex-wrap"
                      >
                        <Input
                          value={agencyName}
                          onChange={(e) => setAgencyName(e.target.value)}
                          placeholder="Mi Agencia de Viajes"
                          data-testid="agency-name-input"
                          className="border-sky-200 flex-1 min-w-[250px]"
                        />
                        <Button
                          type="submit"
                          className="btn-sky text-white"
                          data-testid="save-agency-name-button"
                        >
                          Guardar
                        </Button>
                      </form>
                    </CardContent>
                  </Card>
                </>
              )}

              {/* Emails sub-section */}
              {configTab === "emails" && (
                <>
                  {/* SMTP credentials card */}
                  <Card
                    className="glass-card border-0 mb-5"
                    data-testid="smtp-card"
                  >
                    <CardHeader>
                      <CardTitle className="text-lg font-heading text-sky-900 flex items-center gap-2">
                        <Mail className="w-5 h-5" />
                        Credenciales SMTP (Gmail)
                      </CardTitle>
                      <CardDescription>
                        Necesarias para enviar emails de vouchers y recordatorios.
                        Usá una{" "}
                        <a
                          href="https://myaccount.google.com/apppasswords"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sky-600 hover:underline"
                        >
                          App Password de Google
                        </a>
                        , no tu password normal.
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div>
                        <Label className="text-sky-900 font-medium">
                          Email de Gmail
                        </Label>
                        <Input
                          type="email"
                          value={smtpForm.gmail_user}
                          onChange={(e) =>
                            setSmtpForm((f) => ({ ...f, gmail_user: e.target.value }))
                          }
                          placeholder="contacto@tuagencia.com"
                          className="border-sky-200"
                          data-testid="smtp-user-input"
                        />
                      </div>
                      <div>
                        <Label className="text-sky-900 font-medium">
                          App Password{" "}
                          {smtpInfo.has_password && (
                            <span className="text-xs text-green-700 font-normal">
                              ✓ guardado ({smtpInfo.password_masked}) — dejá vacío
                              para conservar
                            </span>
                          )}
                          {!smtpInfo.has_password && smtpInfo.using_env_fallback && (
                            <span className="text-xs text-amber-700 font-normal">
                              · usando credenciales del entorno
                            </span>
                          )}
                          {!smtpInfo.has_password && !smtpInfo.using_env_fallback && (
                            <span className="text-xs text-red-700 font-normal">
                              · no configurado
                            </span>
                          )}
                        </Label>
                        <Input
                          type="password"
                          value={smtpForm.gmail_app_password}
                          onChange={(e) =>
                            setSmtpForm((f) => ({
                              ...f,
                              gmail_app_password: e.target.value,
                            }))
                          }
                          placeholder={
                            smtpInfo.has_password ? "••••••••" : "16 caracteres sin espacios"
                          }
                          className="border-sky-200 font-mono"
                          data-testid="smtp-password-input"
                        />
                        <p className="text-xs text-sky-600/70 mt-1">
                          En la página de App Passwords de Google, generá uno nuevo
                          para "Mail". Copiá los 16 caracteres sin espacios.
                        </p>
                      </div>
                      <div className="flex gap-2 flex-wrap">
                        <Button
                          onClick={handleSaveSmtp}
                          className="btn-sky text-white"
                          data-testid="save-smtp-button"
                        >
                          <CheckCircle2 className="w-4 h-4 mr-2" />
                          Guardar
                        </Button>
                        <div className="flex gap-2 flex-1 min-w-[280px]">
                          <Input
                            type="email"
                            value={smtpTestEmail}
                            onChange={(e) => setSmtpTestEmail(e.target.value)}
                            placeholder="email@destino.com"
                            className="border-sky-200"
                            data-testid="smtp-test-email-input"
                          />
                          <Button
                            onClick={handleTestSmtp}
                            disabled={smtpLoading}
                            variant="outline"
                            className="border-sky-200 text-sky-700 hover:bg-sky-50 shrink-0"
                            data-testid="smtp-test-button"
                          >
                            {smtpLoading ? (
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            ) : (
                              <Send className="w-4 h-4 mr-2" />
                            )}
                            Enviar prueba
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Avisos: notificación a vendedores cuando el cliente descarga */}
                  <Card className="glass-card border-0 mb-6" data-testid="notify-seller-card">
                    <CardHeader>
                      <CardTitle className="text-lg font-heading text-sky-900 flex items-center gap-2">
                        <Bell className="w-5 h-5" />
                        Avisos a vendedores
                      </CardTitle>
                      <CardDescription>
                        Cuando un cliente descarga un voucher, avisamos por email al vendedor asignado para que sepa que llegaron.
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <label className="flex items-center gap-3 cursor-pointer select-none">
                        <input
                          type="checkbox"
                          checked={notifySellerOnDownload}
                          disabled={notifySellerLoading || (currentAdmin?.role || "admin") !== "admin"}
                          onChange={(e) => handleToggleNotifySeller(e.target.checked)}
                          className="w-4 h-4 accent-sky-600"
                          data-testid="notify-seller-toggle"
                        />
                        <span className="text-sky-900">
                          Enviar aviso por email al vendedor al descargar un voucher
                        </span>
                      </label>
                      <p className="text-xs text-sky-700/70 mt-3">
                        Los avisos usan las credenciales SMTP configuradas arriba. Para evitar spam, no se envía más de un aviso por voucher por hora.
                        {(currentAdmin?.role || "admin") !== "admin" && (
                          <span className="block mt-1 italic">Solo el administrador puede cambiar este ajuste.</span>
                        )}
                      </p>
                    </CardContent>
                  </Card>

                  <p className="text-sm text-sky-700/80 mb-4">
                    Personalizá el asunto y el intro del email. Placeholders:{" "}
                    <code className="bg-sky-50 px-1 rounded">{"{name}"}</code>{" "}
                    <code className="bg-sky-50 px-1 rounded">{"{agency_name}"}</code>{" "}
                    <code className="bg-sky-50 px-1 rounded">{"{emoji}"}</code>
                  </p>
                  {!emailTemplates ? (
                    <Card className="glass-card border-0">
                      <CardContent className="py-8 text-center text-sky-700/60">
                        <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
                        Cargando plantillas...
                      </CardContent>
                    </Card>
                  ) : (
                    <>
                      {[
                        { key: "voucher_subject", label: "Email de vouchers — Asunto", rows: 1 },
                        { key: "voucher_intro", label: "Email de vouchers — Introducción", rows: 4 },
                        { key: "reminder_subject", label: "Email de recordatorio — Asunto", rows: 1 },
                        { key: "reminder_signature", label: "Email de recordatorio — Firma", rows: 2 },
                      ].map((f) => (
                        <Card
                          key={f.key}
                          className="glass-card border-0 mb-4"
                          data-testid={`email-template-${f.key}`}
                        >
                          <CardHeader className="pb-2">
                            <div className="flex items-center justify-between">
                              <CardTitle className="text-sm font-heading text-sky-900">
                                {f.label}
                              </CardTitle>
                              <button
                                type="button"
                                onClick={() => handleResetEmailTemplate(f.key)}
                                className="text-xs text-sky-600 hover:text-sky-800 hover:underline"
                                data-testid={`reset-email-${f.key}`}
                              >
                                Restaurar original
                              </button>
                            </div>
                          </CardHeader>
                          <CardContent>
                            {f.rows === 1 ? (
                              <Input
                                value={emailTemplates[f.key] || ""}
                                onChange={(e) =>
                                  setEmailTemplates((t) => ({
                                    ...t,
                                    [f.key]: e.target.value,
                                  }))
                                }
                                className="border-sky-200"
                                data-testid={`email-input-${f.key}`}
                              />
                            ) : (
                              <Textarea
                                rows={f.rows}
                                value={emailTemplates[f.key] || ""}
                                onChange={(e) =>
                                  setEmailTemplates((t) => ({
                                    ...t,
                                    [f.key]: e.target.value,
                                  }))
                                }
                                className="border-sky-200"
                                data-testid={`email-textarea-${f.key}`}
                              />
                            )}
                          </CardContent>
                        </Card>
                      ))}
                      <Button
                        onClick={handleSaveEmailTemplates}
                        className="btn-sky text-white"
                        data-testid="save-email-templates-button"
                      >
                        <CheckCircle2 className="w-4 h-4 mr-2" />
                        Guardar Plantillas de Email
                      </Button>
                    </>
                  )}
                </>
              )}

              {/* WhatsApp sub-section */}
              {configTab === "whatsapp" && (
                <>
                  <Card className="glass-card border-0 mb-5">
                    <CardHeader>
                      <CardTitle className="text-lg font-heading text-sky-900 flex items-center gap-2">
                        <MessageCircle className="w-5 h-5 text-green-600" />
                        UltraMsg — Envío automático por WhatsApp
                      </CardTitle>
                      <CardDescription>
                        Ingresá tu Instance ID y Token desde{" "}
                        <a
                          href="https://user.ultramsg.com"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sky-600 hover:underline"
                        >
                          user.ultramsg.com
                        </a>
                        . Una vez configurado y activado, el scheduler puede enviar
                        recordatorios por WhatsApp automáticamente.
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div>
                        <Label className="text-sky-900 font-medium">Instance ID</Label>
                        <Input
                          value={ultramsgForm.instance_id}
                          onChange={(e) =>
                            setUltramsgForm((f) => ({
                              ...f,
                              instance_id: e.target.value,
                            }))
                          }
                          placeholder="Ej: instance12345"
                          className="border-sky-200"
                          data-testid="ultramsg-instance-input"
                        />
                      </div>
                      <div>
                        <Label className="text-sky-900 font-medium">
                          Token{" "}
                          {ultramsgInfo.has_token && (
                            <span className="text-xs text-sky-600">
                              (actual: {ultramsgInfo.token_masked}) — dejá vacío
                              para conservar
                            </span>
                          )}
                        </Label>
                        <Input
                          type="password"
                          value={ultramsgForm.token}
                          onChange={(e) =>
                            setUltramsgForm((f) => ({ ...f, token: e.target.value }))
                          }
                          placeholder={
                            ultramsgInfo.has_token
                              ? "••••••••"
                              : "Token de la instancia"
                          }
                          className="border-sky-200"
                          data-testid="ultramsg-token-input"
                        />
                      </div>
                      <label className="flex items-center gap-3 bg-sky-50/50 border border-sky-100 rounded-lg px-4 py-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={ultramsgForm.enabled}
                          onChange={(e) =>
                            setUltramsgForm((f) => ({
                              ...f,
                              enabled: e.target.checked,
                            }))
                          }
                          className="sr-only peer"
                          data-testid="ultramsg-enabled-toggle"
                        />
                        <div className="relative w-11 h-6 bg-sky-200 rounded-full peer peer-checked:bg-green-500 after:content-[''] after:absolute after:top-0.5 after:start-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5" />
                        <div>
                          <p className="text-sm font-medium text-sky-900">
                            Habilitar envío automático por UltraMsg
                          </p>
                          <p className="text-xs text-sky-700/70">
                            El scheduler podrá enviar recordatorios por WhatsApp
                            sin intervención manual.
                          </p>
                        </div>
                      </label>
                      <div className="flex gap-2 flex-wrap">
                        <Button
                          onClick={handleSaveUltramsg}
                          className="btn-sky text-white"
                          data-testid="save-ultramsg-button"
                        >
                          <CheckCircle2 className="w-4 h-4 mr-2" />
                          Guardar
                        </Button>
                        <Button
                          onClick={handleTestUltramsg}
                          variant="outline"
                          className="border-sky-200 text-sky-700 hover:bg-sky-50"
                          data-testid="test-ultramsg-button"
                        >
                          {ultramsgStatus === "loading" ? (
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          ) : null}
                          Verificar conexión
                        </Button>
                      </div>
                      {ultramsgStatus && ultramsgStatus !== "loading" && (
                        <div
                          className={`text-sm rounded-lg px-3 py-2 ${
                            ultramsgStatus.success
                              ? "bg-green-50 text-green-800 border border-green-200"
                              : "bg-red-50 text-red-800 border border-red-200"
                          }`}
                          data-testid="ultramsg-status-display"
                        >
                          {ultramsgStatus.success
                            ? `✓ Conectado — estado: ${ultramsgStatus.status}`
                            : `✗ ${ultramsgStatus.error}`}
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  <Card className="glass-card border-0">
                    <CardContent className="pt-6 text-sm text-sky-700/80 space-y-2">
                      <p className="font-semibold text-sky-900">¿Cómo funciona?</p>
                      <ol className="list-decimal list-inside space-y-1">
                        <li>Creá una cuenta en <a href="https://ultramsg.com" target="_blank" rel="noopener noreferrer" className="text-sky-600 hover:underline">ultramsg.com</a> y generá una instancia.</li>
                        <li>Escaneá el QR con tu WhatsApp para vincular.</li>
                        <li>Copiá el Instance ID y Token acá.</li>
                        <li>Activá el toggle y verificá la conexión.</li>
                        <li>Listo: podrás enviar WhatsApps automáticos desde el diálogo de recordatorio o dejarlo en manos del scheduler.</li>
                      </ol>
                    </CardContent>
                  </Card>
                </>
              )}

              {/* Migrar sub-section */}
              {configTab === "tipos" && <VoucherTypesCard api={API} axiosConfig={axiosConfig} />}

              {configTab === "migrar" && (
                <>
                  <Card className="glass-card border-0 mb-5">
                    <CardHeader>
                      <CardTitle className="text-lg font-heading text-sky-900 flex items-center gap-2">
                        <UploadCloud className="w-5 h-5" />
                        Migrar desde instancia antigua
                      </CardTitle>
                      <CardDescription>
                        Copia clientes, vouchers y logo de otra instancia de esta
                        misma app (preservando IDs y access tokens para que los
                        links viejos sigan funcionando).
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div>
                        <Label className="text-sky-900 font-medium">
                          URL de la instancia vieja
                        </Label>
                        <Input
                          value={migrationForm.old_base}
                          onChange={(e) =>
                            setMigrationForm((f) => ({
                              ...f,
                              old_base: e.target.value,
                            }))
                          }
                          placeholder="https://vouchers.mediaciudadviajes.com"
                          className="border-sky-200"
                          data-testid="migration-url-input"
                        />
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                          <Label className="text-sky-900 font-medium">
                            Usuario
                          </Label>
                          <Input
                            value={migrationForm.old_user}
                            onChange={(e) =>
                              setMigrationForm((f) => ({
                                ...f,
                                old_user: e.target.value,
                              }))
                            }
                            placeholder="tu@email.com"
                            className="border-sky-200"
                            data-testid="migration-user-input"
                          />
                        </div>
                        <div>
                          <Label className="text-sky-900 font-medium">
                            Contraseña
                          </Label>
                          <Input
                            type="password"
                            value={migrationForm.old_pass}
                            onChange={(e) =>
                              setMigrationForm((f) => ({
                                ...f,
                                old_pass: e.target.value,
                              }))
                            }
                            placeholder="••••••••"
                            className="border-sky-200"
                            data-testid="migration-pass-input"
                          />
                        </div>
                      </div>

                      <Button
                        onClick={handleMigrate}
                        disabled={migrationRunning}
                        className="btn-sky text-white w-full"
                        data-testid="migration-run-button"
                      >
                        {migrationRunning ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Migrando... (puede tardar varios minutos, no cierres
                            esta pestaña)
                          </>
                        ) : (
                          <>
                            <UploadCloud className="w-4 h-4 mr-2" />
                            Iniciar migración
                          </>
                        )}
                      </Button>

                      {migrationReport && !migrationReport.error && (
                        <div
                          className="bg-green-50 border border-green-200 rounded-lg p-4 text-sm"
                          data-testid="migration-report"
                        >
                          <p className="font-semibold text-green-900 mb-2">
                            ✓ Migración completada
                          </p>
                          <ul className="space-y-1 text-green-800">
                            <li>
                              Clientes creados:{" "}
                              <strong>{migrationReport.clients_created}</strong>
                            </li>
                            <li>
                              Clientes actualizados:{" "}
                              <strong>{migrationReport.clients_updated}</strong>
                            </li>
                            <li>
                              Vouchers migrados:{" "}
                              <strong>{migrationReport.vouchers_migrated}</strong>
                            </li>
                            <li>
                              Vouchers ya existentes:{" "}
                              <strong>{migrationReport.vouchers_skipped}</strong>
                            </li>
                            <li>
                              Errores (archivos 404):{" "}
                              <strong>{migrationReport.errors}</strong>
                            </li>
                            <li>
                              Logo migrado:{" "}
                              <strong>
                                {migrationReport.logo_migrated ? "sí" : "no"}
                              </strong>
                            </li>
                            {migrationReport.agency_name && (
                              <li>
                                Nombre de agencia:{" "}
                                <strong>{migrationReport.agency_name}</strong>
                              </li>
                            )}
                          </ul>
                          {migrationReport.error_details?.length > 0 && (
                            <details className="mt-2 text-xs text-green-700">
                              <summary className="cursor-pointer">
                                Ver detalles de errores
                              </summary>
                              <ul className="mt-1 space-y-0.5">
                                {migrationReport.error_details.map((e, i) => (
                                  <li key={`migrerr-${i}-${(e || '').slice(0, 30)}`}>· {e}</li>
                                ))}
                              </ul>
                            </details>
                          )}
                        </div>
                      )}
                      {migrationReport?.error && (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-800">
                          <p className="font-semibold">Error: {migrationReport.error}</p>
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  <Card className="glass-card border-0">
                    <CardContent className="pt-6 text-sm text-sky-700/80 space-y-2">
                      <p className="font-semibold text-sky-900">
                        ⚠️ Importante
                      </p>
                      <ul className="list-disc list-inside space-y-1">
                        <li>
                          Solo corré esto <strong>una vez por deploy</strong>. Si
                          lo corrés dos veces los clientes se actualizan (no se
                          duplican), pero los vouchers ya existentes se saltean.
                        </li>
                        <li>
                          La instancia vieja debe seguir accesible online durante
                          la migración.
                        </li>
                        <li>
                          Vouchers cuyo PDF fue borrado del filesystem viejo
                          aparecen como "Errores" — son data loss irreversibles.
                        </li>
                        <li>
                          Tu admin de la instancia vieja sigue funcionando acá;
                          usá las mismas credenciales.
                        </li>
                      </ul>
                    </CardContent>
                  </Card>
                </>
              )}
            </section>
          )}

          {/* ---------- VENDEDORES ---------- */}
          {activeSection === "vendedores" && (currentAdmin?.role || "admin") === "admin" && (
            <VendedoresSection
              users={users}
              usersLoading={usersLoading}
              currentAdmin={currentAdmin}
              onNewUser={openNewUser}
              onEditUser={openEditUser}
              onDeleteUser={handleDeleteUser}
            />
          )}

        </div>
      </main>

      {/* ==================== DIALOGS ==================== */}
      {/* Edit Client Dialog */}
      <Dialog open={editClientDialog} onOpenChange={setEditClientDialog}>
        <DialogContent data-testid="edit-client-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-sky-900 text-xl">
              Editar Cliente
            </DialogTitle>
            <DialogDescription>
              Corregí nombre, apellido, email o teléfono
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSaveEditClient} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label className="text-sky-900 font-medium">Nombre</Label>
                <Input
                  value={editClientForm.name}
                  onChange={(e) =>
                    setEditClientForm((f) => ({ ...f, name: e.target.value }))
                  }
                  required
                  className="border-sky-200"
                  data-testid="edit-client-name-input"
                />
              </div>
              <div>
                <Label className="text-sky-900 font-medium">Apellido</Label>
                <Input
                  value={editClientForm.last_name}
                  onChange={(e) =>
                    setEditClientForm((f) => ({ ...f, last_name: e.target.value }))
                  }
                  className="border-sky-200"
                  data-testid="edit-client-last-name-input"
                />
              </div>
            </div>
            <div>
              <Label className="text-sky-900 font-medium">Email</Label>
              <Input
                type="email"
                value={editClientForm.email}
                onChange={(e) =>
                  setEditClientForm((f) => ({ ...f, email: e.target.value }))
                }
                required
                className="border-sky-200"
                data-testid="edit-client-email-input"
              />
            </div>
            <div>
              <Label className="text-sky-900 font-medium">Teléfono</Label>
              <Input
                type="tel"
                value={editClientForm.phone}
                onChange={(e) =>
                  setEditClientForm((f) => ({ ...f, phone: e.target.value }))
                }
                placeholder="+54 9 223 444 5555"
                className="border-sky-200"
                data-testid="edit-client-phone-input"
              />
            </div>
            <Button
              type="submit"
              className="w-full btn-sky text-white"
              disabled={loading}
              data-testid="save-edit-client-button"
            >
              {loading ? "Guardando..." : "Guardar cambios"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* New Client Dialog */}
      <Dialog open={newClientDialog} onOpenChange={setNewClientDialog}>
        <DialogContent data-testid="new-client-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-sky-900 text-xl">
              Crear Nuevo Cliente
            </DialogTitle>
            <DialogDescription>
              Ingresa los datos del cliente para crear su perfil
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateClient} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label htmlFor="name" className="text-sky-900 font-medium">
                  Nombre
                </Label>
                <Input
                  id="name"
                  name="name"
                  required
                  data-testid="client-name-input"
                  className="border-sky-200"
                />
              </div>
              <div>
                <Label htmlFor="last_name" className="text-sky-900 font-medium">
                  Apellido
                </Label>
                <Input
                  id="last_name"
                  name="last_name"
                  data-testid="client-last-name-input"
                  className="border-sky-200"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="email" className="text-sky-900 font-medium">
                Email
              </Label>
              <Input
                id="email"
                name="email"
                type="email"
                required
                data-testid="client-email-input"
                className="border-sky-200"
              />
            </div>
            <div>
              <Label htmlFor="phone" className="text-sky-900 font-medium">
                Teléfono (opcional, para WhatsApp)
              </Label>
              <Input
                id="phone"
                name="phone"
                type="tel"
                placeholder="+54 9 223 444 5555"
                data-testid="client-phone-input"
                className="border-sky-200"
              />
              <p className="text-xs text-sky-600/70 mt-1">
                Incluí el código de país para poder enviar por WhatsApp.
              </p>
            </div>
            <div className="rounded-xl border border-sky-100 bg-sky-50/50 p-3 space-y-3">
              <p className="text-xs uppercase tracking-wider font-semibold text-sky-700">
                Primer viaje (opcional)
              </p>
              <Input
                name="trip_destination"
                placeholder="Destino. Ej: Cancún"
                data-testid="client-trip-destination-input"
                className="border-sky-200 bg-white"
              />
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-sky-800">Salida</Label>
                  <Input name="trip_date" type="date" data-testid="client-trip-date-input" className="border-sky-200 bg-white" />
                </div>
                <div>
                  <Label className="text-xs text-sky-800">Regreso</Label>
                  <Input name="return_date" type="date" data-testid="client-return-date-input" className="border-sky-200 bg-white" />
                </div>
              </div>
            </div>
            <Button
              type="submit"
              className="w-full btn-sky text-white"
              disabled={loading}
              data-testid="create-client-submit-button"
            >
              {loading ? "Creando..." : "Crear Cliente"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      <TripDialog
        open={tripDialog.open}
        onOpenChange={(o) => !o && setTripDialog({ open: false, trip: null })}
        trip={tripDialog.trip}
        clientName={`${selectedClient?.name || ""} ${selectedClient?.last_name || ""}`.trim()}
        onSave={handleSaveTrip}
      />
      <ReminderPreviewDialog preview={reminderPreview} onClose={() => setReminderPreview(null)} />
      <DuplicateClientDialog
        info={duplicateInfo}
        onConfirm={handleDuplicateAddTrip}
        onCancel={() => setDuplicateInfo(null)}
      />

      {/* Notes Dialog */}
      <Dialog open={notesDialog} onOpenChange={setNotesDialog}>
        <DialogContent data-testid="notes-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-sky-900 text-xl">
              Notas para {selectedClient?.name}
            </DialogTitle>
            <DialogDescription>
              Estas notas serán visibles para el cliente en su panel
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <Textarea
              value={clientNotes}
              onChange={(e) => setClientNotes(e.target.value)}
              placeholder="Escribe notas o instrucciones para el cliente..."
              rows={6}
              data-testid="notes-textarea"
              className="border-sky-200"
            />
            <Button
              onClick={handleUpdateNotes}
              disabled={loading}
              className="w-full btn-sky text-white"
              data-testid="save-notes-button"
            >
              {loading ? "Guardando..." : "Guardar Notas"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Voucher Upload Dialog */}
      <Dialog
        open={voucherDialog}
        onOpenChange={(open) => {
          if (!open) {
            setSelectedFiles([]);
            setUploadProgress({});
            setVoucherType("");
          }
          setVoucherDialog(open);
        }}
      >
        <DialogContent
          data-testid="upload-voucher-dialog"
          className="sm:max-w-lg"
        >
          <DialogHeader>
            <DialogTitle className="font-heading text-sky-900 text-xl">
              Subir Vouchers
            </DialogTitle>
            <DialogDescription>
              Arrastra uno o varios PDFs — se subirán todos a la vez.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUploadVouchers} className="space-y-4">
            <div>
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <Label className="text-sky-900 font-medium">Tipo de Voucher</Label>
                <label className="flex items-center gap-2 text-xs text-sky-700 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={autoDetectType}
                    onChange={(e) => setAutoDetectType(e.target.checked)}
                    data-testid="auto-detect-checkbox"
                  />
                  Detectar automáticamente por nombre de archivo
                </label>
              </div>
              <Select
                value={voucherType}
                onValueChange={setVoucherType}
                disabled={autoDetectType}
              >
                <SelectTrigger
                  data-testid="voucher-type-select"
                  className={`border-sky-200 ${autoDetectType ? "opacity-50" : ""}`}
                >
                  <SelectValue placeholder={autoDetectType ? "Se detecta por nombre" : "Selecciona el tipo"} />
                </SelectTrigger>
                <SelectContent>
                  {voucherTypes.types.map((type) => (
                    <SelectItem key={type.key} value={type.key}>
                      <span className="flex items-center gap-2">
                        <VoucherTypeIcon icon={type.icon} className="w-4 h-4 text-sky-600" />
                        {type.label}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {autoDetectType && (
                <p className="text-xs text-sky-600/70 mt-1">
                  Se reconoce por las palabras clave de cada tipo (editables en Configuración → Tipos de servicio).
                </p>
              )}
            </div>
            <div
              onDragEnter={handleDrag}
              onDragOver={handleDrag}
              onDragLeave={handleDrag}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all ${
                dragActive
                  ? "dropzone-active"
                  : "border-sky-200 bg-sky-50/50 hover:bg-sky-50 hover:border-sky-300"
              }`}
              data-testid="voucher-dropzone"
            >
              <UploadCloud className="w-10 h-10 mx-auto text-sky-500 mb-2" />
              <p className="text-sm font-medium text-sky-900">
                Arrastra PDFs aquí o haz clic para seleccionar
              </p>
              <p className="text-xs text-sky-600/70 mt-1">
                Puedes subir varios archivos a la vez
              </p>
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf"
                multiple
                onChange={(e) => addFiles(e.target.files)}
                className="hidden"
                data-testid="voucher-file-input"
              />
            </div>
            {selectedFiles.length > 0 && (
              <div className="max-h-48 overflow-y-auto space-y-2 border border-sky-100 rounded-lg p-2 bg-white/70">
                {selectedFiles.map((file, idx) => {
                  const status = uploadProgress[idx];
                  return (
                    <div
                      key={`${file.name}-${file.size}-${idx}`}
                      className="flex items-center justify-between gap-2 px-2 py-1.5 rounded hover:bg-sky-50"
                      data-testid={`voucher-file-item-${idx}`}
                    >
                      <div className="flex items-center gap-2 min-w-0 flex-1">
                        <FileText className="w-4 h-4 text-sky-500 shrink-0" />
                        <div className="min-w-0 flex-1">
                          <p className="text-sm text-sky-900 truncate">
                            {file.name}
                          </p>
                          <p className="text-xs text-sky-600/70">
                            {formatFileSize(file.size)}
                          </p>
                        </div>
                      </div>
                      <div className="shrink-0">
                        {status === "uploading" && (
                          <Loader2 className="w-4 h-4 text-sky-600 animate-spin" />
                        )}
                        {status === "done" && (
                          <CheckCircle2 className="w-4 h-4 text-green-600" />
                        )}
                        {status === "error" && (
                          <span className="text-xs text-red-600">Error</span>
                        )}
                        {!status && (
                          <button
                            type="button"
                            onClick={() => removeFile(idx)}
                            className="text-sky-400 hover:text-red-500"
                            data-testid={`remove-file-${idx}`}
                          >
                            <X className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            <Button
              type="submit"
              className="w-full btn-sky text-white"
              disabled={loading || selectedFiles.length === 0}
              data-testid="upload-voucher-submit-button"
            >
              {loading
                ? `Subiendo ${selectedFiles.length} archivo${
                    selectedFiles.length > 1 ? "s" : ""
                  }...`
                : selectedFiles.length > 0
                ? `Subir ${selectedFiles.length} voucher${
                    selectedFiles.length > 1 ? "s" : ""
                  }`
                : "Subir Vouchers"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Reminder Dialog */}
      <Dialog open={reminderDialog} onOpenChange={setReminderDialog}>
        <DialogContent
          data-testid="reminder-dialog"
          className="sm:max-w-2xl max-h-[90vh] overflow-y-auto"
        >
          <DialogHeader>
            <DialogTitle className="font-heading text-sky-900 text-xl flex items-center gap-2">
              <Bell className="w-5 h-5" />
              Recordatorio — {selectedClient?.name} · {tripTitle(selectedTrip)}
            </DialogTitle>
            <DialogDescription>
              Configurá el recordatorio pre viaje. Elegí una plantilla (o se usa la General) y
              editá el mensaje libremente; las variables se completan al enviar.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-sky-900 font-medium">
                Plantilla por destino{" "}
                <span className="text-xs text-sky-600/70 font-normal">
                  (opcional — sin elegir se usa la General)
                </span>
              </Label>
              <Select
                value={reminderForm.destination_template_id || "__general__"}
                onValueChange={async (id) => {
                  if (id === "__general__") {
                    setReminderForm((f) => ({ ...f, destination_template_id: "" }));
                    const res = await axios.get(`${API}/settings/general-template`, axiosConfig);
                    setReminderForm((f) => ({ ...f, reminder_message: res.data.template }));
                    return;
                  }
                  const dt = destTemplates.find((x) => x.id === id);
                  setReminderForm((f) => ({
                    ...f,
                    destination_template_id: id,
                    trip_destination: dt && dt.kind !== "general" ? dt.destination : f.trip_destination,
                    reminder_message: dt ? dt.message : f.reminder_message,
                  }));
                }}
              >
                <SelectTrigger
                  className="border-sky-200"
                  data-testid="dest-template-select"
                >
                  <SelectValue placeholder="Elegí un destino guardado..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__general__">General (por defecto)</SelectItem>
                  {destTemplates.map((dt) => (
                    <SelectItem key={dt.id} value={dt.id}>
                      {templateLabel(dt)}
                      {dt.kind === "general" ? " · general" : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label className="text-sky-900 font-medium">
                  Días antes del viaje
                </Label>
                <Input
                  type="number"
                  min="1"
                  max="60"
                  value={reminderForm.reminder_days_before}
                  onChange={(e) =>
                    setReminderForm((f) => ({
                      ...f,
                      reminder_days_before: e.target.value,
                    }))
                  }
                  className="border-sky-200"
                  data-testid="reminder-days-input"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <Label className="text-sky-900 font-medium">Fecha de salida</Label>
                <Input
                  type="date"
                  value={reminderForm.trip_date}
                  onChange={(e) =>
                    setReminderForm((f) => ({ ...f, trip_date: e.target.value }))
                  }
                  className="border-sky-200"
                  data-testid="trip-date-input"
                />
              </div>
              <div>
                <Label className="text-sky-900 font-medium">Fecha de regreso</Label>
                <Input
                  type="date"
                  value={reminderForm.return_date}
                  min={reminderForm.trip_date || undefined}
                  onChange={(e) =>
                    setReminderForm((f) => ({ ...f, return_date: e.target.value }))
                  }
                  className="border-sky-200"
                  data-testid="trip-return-date-input"
                />
              </div>
              <div>
                <Label className="text-sky-900 font-medium">Destino</Label>
                <Input
                  value={reminderForm.trip_destination}
                  onChange={(e) =>
                    setReminderForm((f) => ({
                      ...f,
                      trip_destination: e.target.value,
                    }))
                  }
                  placeholder="Ej: Río de Janeiro, Cancún"
                  className="border-sky-200"
                  data-testid="trip-destination-input"
                />
              </div>
            </div>
            <div>
              <Label className="text-sky-900 font-medium">
                Detalles adicionales
              </Label>
              <Input
                value={reminderForm.trip_details}
                onChange={(e) =>
                  setReminderForm((f) => ({ ...f, trip_details: e.target.value }))
                }
                placeholder="Aeropuerto de salida, primera parada, etc."
                className="border-sky-200"
                data-testid="trip-details-input"
              />
              <p className="text-xs text-sky-600/70 mt-1">
                Reemplaza <code>{"{detalles}"}</code> en la plantilla.
              </p>
            </div>
            <div>
              <Label className="text-sky-900 font-medium">
                Teléfono (WhatsApp)
              </Label>
              <Input
                type="tel"
                value={reminderForm.phone}
                onChange={(e) =>
                  setReminderForm((f) => ({ ...f, phone: e.target.value }))
                }
                placeholder="+54 9 223 444 5555"
                className="border-sky-200"
                data-testid="reminder-phone-input"
              />
              <p className="text-xs text-sky-600/70 mt-1">
                Con código de país. Requerido para enviar por WhatsApp.
              </p>
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <Label className="text-sky-900 font-medium">
                  Emails adicionales (recibirán una copia del recordatorio)
                </Label>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() =>
                    setReminderForm((f) => ({
                      ...f,
                      extra_emails: [...(f.extra_emails || []), ""],
                    }))
                  }
                  className="border-sky-300 text-sky-700 hover:bg-sky-50 h-7 text-xs"
                  data-testid="add-extra-email-button"
                >
                  + Agregar email
                </Button>
              </div>
              {(reminderForm.extra_emails || []).length === 0 ? (
                <p className="text-xs text-sky-600/70">
                  Solo se enviará al email principal del cliente.
                </p>
              ) : (
                <div className="space-y-2">
                  {reminderForm.extra_emails.map((em, idx) => (
                    <div key={idx} className="flex gap-2" data-testid={`extra-email-row-${idx}`}>
                      <Input
                        type="email"
                        value={em}
                        onChange={(e) => {
                          const next = [...reminderForm.extra_emails];
                          next[idx] = e.target.value;
                          setReminderForm((f) => ({ ...f, extra_emails: next }));
                        }}
                        placeholder="otro@email.com"
                        className="border-sky-200"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() =>
                          setReminderForm((f) => ({
                            ...f,
                            extra_emails: f.extra_emails.filter((_, i) => i !== idx),
                          }))
                        }
                        className="border-red-200 text-red-600 hover:bg-red-50 shrink-0 px-2"
                        data-testid={`remove-extra-email-${idx}`}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div>
              <Label className="text-sky-900 font-medium">
                Teléfono adicional (WhatsApp)
              </Label>
              <Input
                type="tel"
                value={reminderForm.phone_secondary}
                onChange={(e) =>
                  setReminderForm((f) => ({ ...f, phone_secondary: e.target.value }))
                }
                placeholder="+54 9 223 555 5555"
                className="border-sky-200"
                data-testid="reminder-phone-secondary-input"
              />
              <p className="text-xs text-sky-600/70 mt-1">
                Segundo contacto (ej. cónyuge, familiar).
              </p>
            </div>

            <div>
              <div className="flex items-center justify-between mb-1">
                <Label className="text-sky-900 font-medium">
                  Mensaje del recordatorio
                </Label>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={handleGenerateTemplate}
                  className="border-sky-300 text-sky-700 hover:bg-sky-50 h-8"
                  data-testid="generate-template-button"
                >
                  <Sparkles className="w-3.5 h-3.5 mr-1" />
                  Cargar plantilla
                </Button>
              </div>
              <RichTextEditor
                value={reminderForm.reminder_message}
                onChange={(v) => setReminderForm((f) => ({ ...f, reminder_message: v }))}
                axiosConfig={axiosConfig}
                minHeight={120}
                testId="reminder-message-editor"
              />
              <div className="flex items-start justify-between gap-3 mt-2 flex-wrap">
                <TemplateVarsHint compact />
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={handlePreviewReminder}
                  className="border-sky-300 text-sky-700 hover:bg-sky-50 h-8 shrink-0"
                  data-testid="reminder-preview-button"
                >
                  <Eye className="w-3.5 h-3.5 mr-1" />
                  Vista previa
                </Button>
              </div>
            </div>
            <div>
              <Label className="text-sky-900 font-medium mb-2 block">
                Canal de envío automático
              </Label>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { value: "email", label: "Email", Icon: Mail },
                  { value: "whatsapp", label: "WhatsApp", Icon: MessageCircle },
                  { value: "both", label: "Ambos", Icon: Send },
                ].map((opt) => {
                  const isActive = reminderForm.reminder_channel === opt.value;
                  const Icon = opt.Icon;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() =>
                        setReminderForm((f) => ({
                          ...f,
                          reminder_channel: opt.value,
                        }))
                      }
                      data-testid={`reminder-channel-${opt.value}`}
                      className={`flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium transition-all border-2 ${
                        isActive
                          ? "bg-sky-600 border-sky-600 text-white shadow-sm"
                          : "bg-white border-sky-200 text-sky-700 hover:bg-sky-50 hover:border-sky-300"
                      }`}
                    >
                      <Icon className="w-4 h-4" />
                      {opt.label}
                    </button>
                  );
                })}
              </div>
              <p className="text-xs text-sky-600/70 mt-2">
                {reminderForm.reminder_channel === "email" &&
                  "Se enviará por email cuando se active el envío automático."}
                {reminderForm.reminder_channel === "whatsapp" &&
                  "Se enviará por WhatsApp (requiere UltraMsg configurado + teléfono del cliente)."}
                {reminderForm.reminder_channel === "both" &&
                  "Se enviará por email y WhatsApp simultáneamente."}
              </p>
            </div>

            <div className="flex items-center justify-between bg-sky-50/50 border border-sky-100 rounded-lg px-4 py-3">
              <div>
                <p className="text-sm font-medium text-sky-900">
                  Envío automático activado
                </p>
                <p className="text-xs text-sky-700/70">
                  Se enviará el día exacto según "Días antes del viaje".
                </p>
              </div>
              <label className="inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={reminderForm.reminder_enabled}
                  onChange={(e) =>
                    setReminderForm((f) => ({
                      ...f,
                      reminder_enabled: e.target.checked,
                    }))
                  }
                  className="sr-only peer"
                  data-testid="reminder-enabled-toggle"
                />
                <div className="relative w-11 h-6 bg-sky-200 peer-focus:outline-none rounded-full peer peer-checked:bg-sky-600 after:content-[''] after:absolute after:top-0.5 after:start-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5"></div>
              </label>
            </div>
            {reminderSentAt && (
              <p className="text-xs text-sky-700/80">
                Último envío: {new Date(reminderSentAt).toLocaleString()}
              </p>
            )}
            <div className="flex gap-2 pt-2 flex-wrap">
              <Button
                type="button"
                onClick={handleSaveReminder}
                disabled={loading}
                className="flex-1 min-w-[120px] btn-sky text-white"
                data-testid="save-reminder-button"
              >
                {loading ? "Guardando..." : "Guardar"}
              </Button>
              <Button
                type="button"
                onClick={handleSendReminderNow}
                disabled={loading}
                variant="outline"
                className="flex-1 min-w-[140px] border-sky-300 text-sky-700 hover:bg-sky-50"
                data-testid="send-reminder-now-button"
              >
                <Mail className="w-4 h-4 mr-2" />
                Enviar por Email
              </Button>
              <Button
                type="button"
                onClick={handleSendReminderWhatsApp}
                disabled={loading}
                className="flex-1 min-w-[140px] bg-green-500 hover:bg-green-600 text-white"
                data-testid="send-reminder-whatsapp-button"
              >
                <MessageCircle className="w-4 h-4 mr-2" />
                WhatsApp manual
              </Button>
              {ultramsgInfo.enabled && (
                <Button
                  type="button"
                  onClick={handleSendReminderUltraMsg}
                  disabled={loading}
                  className="flex-1 min-w-[140px] bg-green-700 hover:bg-green-800 text-white"
                  data-testid="send-reminder-ultramsg-button"
                >
                  <Send className="w-4 h-4 mr-2" />
                  WhatsApp automático
                </Button>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* User (Vendedor) Dialog */}
      <UserDialog
        open={userDialog}
        onOpenChange={setUserDialog}
        editingUser={editingUser}
        userForm={userForm}
        setUserForm={setUserForm}
        onSave={handleSaveUser}
      />

    </div>
  );
}
